import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { format as formatDateFns } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItemButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { Iconify, LightboxModal } from '@/components';
import { getBookingSession } from '@/features/bookings-management/api';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import type {
  BookingSession,
  BookingSessionStatus,
  BookingUser,
} from '@/features/bookings-management/types';
import { returnSessionFilterFromDate } from '@/features/bookings-management/utils/bookingSessions';
import RightSidebarTitle from '@/features/app/components/right-sidebar/right-sidebar-partials/RightSidebarTitle';
import { getDashboardUsers } from '@/features/users/shared/api';
import type { User } from '@/features/users/shared/types';
import useAuth from '@/hooks/useAuth';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { dispatch, useSelector } from '@/stores';
import { formatAddress } from '@/utils/formatAddress';
import { fDate, fDateTime, fToNow } from '@/utils/formatTime';

import {
  CUSTOM_CHAT_CHANNELS_ACTIVE_POLL_INTERVAL,
  customChatChannelQueryKey,
  useArchiveCustomChatChannelMutation,
  useAddCustomChatParticipantsMutation,
  useCustomChatChannels,
  useCustomChatMessages,
  useMarkCustomChatReadMutation,
  useOpenCustomChatChannelMutation,
  usePickUpCustomChatChannelMutation,
  useRemoveCustomChatParticipantMutation,
  useSendCustomChatMessageMutation,
  useSendCustomChatGeneralMessageMutation,
  useSetCustomChatExternalLockMutation,
  useTransferCustomChatChannelMutation,
} from '../hooks';
import {
  clearCustomChatOpenRequest,
  setCustomChatActiveChannelId,
  updateCustomChatUnreadMessages,
} from '../slices';
import {
  getCustomChatAttachmentAccessUrl,
  uploadCustomChatAttachment,
} from '../api';
import type {
  CustomChatAttachment,
  CustomChatChannel,
  CustomChatChannelType,
  CustomChatLink,
  CustomChatMention,
  CustomChatMessage,
  CustomChatParticipantIdentity,
  CustomChatParticipant,
  CustomChatParticipantType,
} from '../types';
import {
  CUSTOM_CHAT_JOB_SUPPORT_GROUPS,
  getCustomChatJobSupportSessionIdsToFetch,
  groupCustomChatJobSupportChannels,
  sortCustomChatJobSupportGroupItems,
  type GroupedCustomChatJobSupportChannel,
} from '../utils/jobSupportGroups';
import {
  CUSTOM_CHAT_PARTICIPANT_ROLE_LABELS,
  getCustomChatMessageDisplayText,
  getCustomChatSenderRoleLabel,
  getCustomChatTranslatedMessageInfo,
  groupCustomChatMessages,
  isCustomChatGeneralMessage,
} from '../utils/messages';

const CHANNEL_TYPE_LABELS: Record<CustomChatChannelType, string> = {
  cleanerSupport: 'Cleaner Support',
  clientSupport: 'Client Support',
  dashboardSupport: 'Dashboard Support',
  directMessage: 'Direct Message',
  jobSupport: 'Session Support',
};

type CustomChatNavView = 'channels' | 'directMessages';

type CustomChatMessageDay = {
  groups: ReturnType<typeof groupCustomChatMessages>;
  id: string;
  label: string;
};

const MAX_CHAT_ATTACHMENTS = 5;
const ATTACHMENT_ACCESS_URL_STALE_TIME_MS = 55 * 60 * 1000;
const ACTIVE_RECENTLY_THRESHOLD_MS = 5 * 60 * 1000;
const FOCUSED_CHAT_STATUS_REFRESH_THROTTLE_MS = 30000;
const LOAD_OLDER_MESSAGES_SCROLL_THRESHOLD_PX = 48;
const SCROLL_TO_LATEST_BOTTOM_THRESHOLD_PX = 96;

const SUPPORT_CHANNEL_SECTIONS: Array<{
  alwaysShow?: boolean;
  emptyText: string;
  type: Exclude<CustomChatChannelType, 'directMessage'>;
}> = [
  {
    alwaysShow: true,
    emptyText: 'No client support chats yet.',
    type: 'clientSupport',
  },
  {
    alwaysShow: true,
    emptyText: 'No cleaner support chats yet.',
    type: 'cleanerSupport',
  },
  {
    emptyText: 'No dashboard support chats yet.',
    type: 'dashboardSupport',
  },
  {
    alwaysShow: true,
    emptyText: 'No session support chats yet.',
    type: 'jobSupport',
  },
];

const getChannelTitle = (channel?: CustomChatChannel | null) =>
  channel?.title?.trim() ||
  (channel?.type ? CHANNEL_TYPE_LABELS[channel.type] : 'Chat');

const getLastMessageText = (message?: CustomChatMessage | null) => {
  const text = getCustomChatMessageDisplayText(message).trim();
  if (text) return text;
  if (message?.attachments?.length) {
    return `${message.attachments.length} attachment${
      message.attachments.length === 1 ? '' : 's'
    }`;
  }
  return 'No messages yet';
};

const isSafeAttachmentUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  } catch {
    return false;
  }
};

const isImageAttachment = (attachment: CustomChatAttachment) => {
  if (attachment.content_type?.toLowerCase().startsWith('image/')) {
    return true;
  }

  try {
    const pathname = new URL(attachment.asset_url).pathname.toLowerCase();
    return /\.(apng|avif|gif|jpe?g|png|webp)$/i.test(pathname);
  } catch {
    return false;
  }
};

type CustomChatGalleryAttachment = {
  attachment: CustomChatAttachment;
  attachmentIndex: number;
  key: string;
  label: string;
  message: CustomChatMessage;
};

type CustomChatGalleryImage = CustomChatGalleryAttachment & {
  accessUrl: string;
};

type SelectedChatGalleryImage = {
  accessUrl: string;
  key: string;
};

const getChatImageGalleryKey = (
  message: Pick<CustomChatMessage, 'id'>,
  attachment: CustomChatAttachment,
  attachmentIndex: number
) =>
  `${message.id}:${attachment.id ?? attachment.asset_url ?? attachmentIndex}`;

const formatDate = (date?: string | null) => {
  if (!date) return '';
  try {
    return fDateTime(date);
  } catch {
    return date;
  }
};

const getValidMessageDate = (date?: string | null) => {
  if (!date) return null;

  const messageDate = new Date(date);

  return Number.isFinite(messageDate.getTime()) ? messageDate : null;
};

const formatMessageTime = (date?: string | null) => {
  const messageDate = getValidMessageDate(date);

  if (!messageDate) return date ?? '';

  return formatDateFns(messageDate, 'hh:mm a');
};

const getMessageDayKey = (date?: string | null) => {
  const messageDate = getValidMessageDate(date);

  return messageDate ? formatDateFns(messageDate, 'yyyy-MM-dd') : 'unknown';
};

const getMessageDayLabel = (date?: string | null) => {
  const messageDate = getValidMessageDate(date);

  return messageDate ? formatDateFns(messageDate, 'EEEE, MMMM do') : 'Unknown';
};

const groupCustomChatMessagesByDay = (
  messages: CustomChatMessage[]
): CustomChatMessageDay[] => {
  const buckets = messages.reduce<
    Array<{ id: string; label: string; messages: CustomChatMessage[] }>
  >((days, message) => {
    const id = getMessageDayKey(message.created_at);
    const previousDay = days[days.length - 1];

    if (previousDay?.id === id) {
      previousDay.messages.push(message);
      return days;
    }

    days.push({
      id,
      label: getMessageDayLabel(message.created_at),
      messages: [message],
    });

    return days;
  }, []);

  return buckets.map((bucket) => ({
    groups: groupCustomChatMessages(bucket.messages),
    id: bucket.id,
    label: bucket.label,
  }));
};

const getTimestamp = (date?: string | null) => {
  if (!date) return null;

  const timestamp = new Date(date).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

type CustomChatJobDetailRow = {
  label: string;
  value: string;
};

type CustomChatJobContextLinks = {
  bookingPath: string;
  sessionPath?: string;
};

type CustomChatStatusTone =
  | 'default'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

const EMPTY_JOB_VALUE = 'N/A';
const SESSION_FILTERS = ['previous', 'today', 'upcoming'];

const coercePositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getMetadataValue = (
  metadata: Record<string, unknown>,
  keys: string[]
): unknown => {
  for (const key of keys) {
    const value = metadata[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return value;
    }
  }

  return null;
};

const getMetadataString = (
  metadata: Record<string, unknown>,
  keys: string[]
): string => {
  const value = getMetadataValue(metadata, keys);
  return value === null ? EMPTY_JOB_VALUE : String(value);
};

const getMetadataNumber = (
  metadata: Record<string, unknown>,
  keys: string[]
): number | null => {
  for (const key of keys) {
    const parsed = coercePositiveNumber(metadata[key]);
    if (parsed) return parsed;
  }

  return null;
};

const getStatusTone = (
  status?: BookingSessionStatus | null
): CustomChatStatusTone => {
  const styleClass = status?.style_class?.toLowerCase();

  if (styleClass === 'success' || styleClass === 'warning') return styleClass;
  if (styleClass === 'error') return 'error';
  if (styleClass === 'info' || styleClass === 'primary') return 'info';

  return 'default';
};

const getChipColor = (tone: CustomChatStatusTone) =>
  tone === 'default' ? 'default' : tone;

const formatJobName = (user?: Partial<BookingUser>): string => {
  if (!user) return EMPTY_JOB_VALUE;

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || EMPTY_JOB_VALUE;
};

const formatSessionDate = (value?: string | null): string => {
  if (!value) return EMPTY_JOB_VALUE;

  try {
    return fDate(value, 'dd MMM yyyy');
  } catch {
    return value;
  }
};

const formatJobAddress = (session: BookingSession) => {
  const address = session.address;
  if (!address) return EMPTY_JOB_VALUE;

  if (address.formatted_address) {
    return formatAddress(address.formatted_address, address.postcode)
      .formattedAddressWithPostcode;
  }

  return (
    [address.line_1, address.line_2, address.town_or_city, address.postcode]
      .filter(Boolean)
      .join(', ') || EMPTY_JOB_VALUE
  );
};

const formatJobDisplayValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return EMPTY_JOB_VALUE;
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (Array.isArray(value)) {
    return value.length
      ? value.map(formatJobDisplayValue).join(', ')
      : EMPTY_JOB_VALUE;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const display =
      record.display ??
      record.name ??
      record.label ??
      record.value ??
      record.ref ??
      null;

    return display ? String(display) : EMPTY_JOB_VALUE;
  }

  return String(value);
};

const formatJobHours = (value: unknown): string => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return EMPTY_JOB_VALUE;
  if (parsed === 1) return '1 hour';
  return `${parsed} hours`;
};

const getCleanerName = (session: BookingSession): string => {
  const firstCleaner = session.booking_session_cleaners?.[0];
  return firstCleaner ? formatJobName(firstCleaner) : 'Unassigned';
};

const getServiceName = (session: BookingSession): string => {
  const service = session.service_variant?.service?.name;
  const variant = session.service_variant?.name;

  return [service, variant].filter(Boolean).join(' - ') || EMPTY_JOB_VALUE;
};

const getSessionFilter = (date: unknown): string => {
  if (!date || String(date) === EMPTY_JOB_VALUE) return 'upcoming';

  try {
    const filter = returnSessionFilterFromDate(String(date));
    return SESSION_FILTERS.includes(filter) ? filter : 'upcoming';
  } catch {
    return 'upcoming';
  }
};

const resolveCustomChatBookingSessionId = (
  channel?: CustomChatChannel | null
): number | null => {
  if (!channel || channel.type !== 'jobSupport') return null;

  const metadata = channel.metadata ?? {};
  return (
    getMetadataNumber(metadata, [
      'bookingSessionId',
      'sessionId',
      'booking_session_id',
      'id',
    ]) ?? coercePositiveNumber(channel.booking_session_id)
  );
};

const buildCustomChatJobLinks = (
  session: BookingSession | undefined,
  metadata: Record<string, unknown>,
  bookingSessionId: number | null
): CustomChatJobContextLinks | null => {
  const bookingId =
    coercePositiveNumber(session?.booking_id) ??
    getMetadataNumber(metadata, ['bookingId', 'booking_id']);

  if (!bookingId) return null;

  const sessionId = coercePositiveNumber(session?.id) ?? bookingSessionId;
  const sessionFilter = getSessionFilter(
    session?.date ?? getMetadataString(metadata, ['bookingDate', 'date'])
  );
  const bookingPath = bookingsManagementPath('view', {
    bookingId: String(bookingId),
    bookingTabSid: 'booking-sessions',
    sessionFilter,
  });

  return {
    bookingPath,
    sessionPath: sessionId
      ? `${bookingPath}?sessionID=${sessionId}`
      : undefined,
  };
};

const getJobDetailRows = (
  session: BookingSession
): CustomChatJobDetailRow[] => [
  { label: 'Client', value: formatJobName(session.user) },
  { label: 'Cleaner', value: getCleanerName(session) },
  { label: 'Date', value: formatSessionDate(session.date) },
  { label: 'Time', value: session.time || EMPTY_JOB_VALUE },
  { label: 'Address', value: formatJobAddress(session) },
  { label: 'Access type', value: formatJobDisplayValue(session.access_type) },
  { label: 'Service', value: getServiceName(session) },
  { label: 'Property', value: formatJobDisplayValue(session.property_type) },
  { label: 'Bedrooms', value: formatJobDisplayValue(session.bedrooms) },
  { label: 'Bathrooms', value: formatJobDisplayValue(session.bathrooms) },
  {
    label: 'Hours',
    value: formatJobHours(session.total_hours ?? session.hours),
  },
  {
    label: 'Cleaning products',
    value: formatJobDisplayValue(session.cleaning_products),
  },
  { label: 'Extras', value: formatJobDisplayValue(session.extras) },
  { label: 'Pets', value: formatJobDisplayValue(session.pets) },
];

const getJobNotes = (session: BookingSession): CustomChatJobDetailRow[] =>
  [
    {
      label: 'Access instructions',
      value: formatJobDisplayValue(session.access_instructions),
    },
    {
      label: 'Provider instructions',
      value: formatJobDisplayValue(session.provider_instructions),
    },
    { label: 'Admin notes', value: formatJobDisplayValue(session.admin_notes) },
    {
      label: 'Internal notes',
      value: formatJobDisplayValue(session.internal_notes),
    },
  ].filter(({ value }) => value && value !== EMPTY_JOB_VALUE);

const uploadChatFile = async (file: File): Promise<CustomChatAttachment> => {
  const formData = new FormData();
  formData.append('file', file);

  const attachment = await uploadCustomChatAttachment(formData);

  if (!isSafeAttachmentUrl(attachment.asset_url)) {
    throw new Error('Upload did not return a safe file URL');
  }

  return attachment;
};

const createClientMessageId = () => {
  const crypto = globalThis.crypto as { randomUUID?: () => string } | undefined;

  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}_${Math.round(Math.random() * 100000)}`;
};

const getUserOptionLabel = (user: User) => {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  const display = name || user.email || `User ${user.id}`;

  return `${display} (#${user.id})`;
};

type DisplayNameSource = {
  displayName?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  profile_image?: string | number | null;
  user?: DisplayNameSource | null;
};

const getPersonDisplayName = (
  person?: DisplayNameSource | null
): string | null => {
  const nestedUserName: string | null =
    person?.user && person.user !== person
      ? getPersonDisplayName(person.user)
      : null;
  const fullName = [person?.first_name, person?.last_name]
    .filter(Boolean)
    .map((name) => String(name).trim())
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    fullName ||
    person?.displayName?.trim() ||
    person?.name?.trim() ||
    person?.email?.trim() ||
    nestedUserName ||
    null
  );
};

const getProfileImageUrl = (
  person?: DisplayNameSource | null
): string | undefined => {
  const profileImage = person?.profile_image;

  if (typeof profileImage !== 'string') {
    return person?.user && person.user !== person
      ? getProfileImageUrl(person.user)
      : undefined;
  }

  const trimmedProfileImage = profileImage.trim();

  return (
    trimmedProfileImage ||
    (person?.user && person.user !== person
      ? getProfileImageUrl(person.user)
      : undefined)
  );
};

const isSameParticipantIdentity = (
  firstParticipant:
    | Pick<CustomChatParticipant, 'participant_id' | 'participant_type'>
    | null
    | undefined,
  secondParticipant:
    | Pick<CustomChatParticipant, 'participant_id' | 'participant_type'>
    | null
    | undefined
) =>
  Boolean(
    firstParticipant &&
      secondParticipant &&
      firstParticipant.participant_type ===
        secondParticipant.participant_type &&
      Number(firstParticipant.participant_id) ===
        Number(secondParticipant.participant_id)
  );

const getCurrentChannelParticipant = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null
) => {
  if (channel.current_participant) return channel.current_participant;

  return (
    channel.participants?.find(
      (participant) =>
        participant.participant_type === 'dashboard_user' &&
        Number(participant.participant_id) === currentDashboardUserId
    ) ?? null
  );
};

const isCurrentChannelParticipant = (
  participant: CustomChatParticipant,
  currentParticipant: CustomChatParticipant | null,
  currentDashboardUserId: number | null
) => {
  if (currentParticipant) {
    return isSameParticipantIdentity(participant, currentParticipant);
  }

  return (
    participant.participant_type === 'dashboard_user' &&
    Number(participant.participant_id) === currentDashboardUserId
  );
};

const isMessageFromParticipant = (
  message: CustomChatMessage,
  participant: CustomChatParticipant | null
) =>
  Boolean(
    participant &&
      message.sender_type === participant.participant_type &&
      Number(message.sender_id) === Number(participant.participant_id)
  );

const isCurrentDashboardMessage = (
  message: CustomChatMessage,
  currentParticipant: CustomChatParticipant | null,
  currentDashboardUserId: number | null
) => {
  if (currentParticipant)
    return isMessageFromParticipant(message, currentParticipant);

  return (
    message.sender_type === 'dashboard_user' &&
    Number(message.sender_id) === currentDashboardUserId
  );
};

const getDirectMessageTitle = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null
) => {
  const currentParticipant = getCurrentChannelParticipant(
    channel,
    currentDashboardUserId
  );
  const otherParticipantNames = (channel.participants ?? [])
    .filter(
      (participant) =>
        !isCurrentChannelParticipant(
          participant,
          currentParticipant,
          currentDashboardUserId
        )
    )
    .map((participant) => getPersonDisplayName(participant))
    .filter(Boolean);

  return otherParticipantNames.length
    ? otherParticipantNames.join(', ')
    : getChannelTitle(channel);
};

const getDirectMessageAvatarParticipant = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null
) => {
  const participants = channel.participants ?? [];
  const currentParticipant = getCurrentChannelParticipant(
    channel,
    currentDashboardUserId
  );
  const otherParticipants = participants.filter(
    (participant) =>
      !isCurrentChannelParticipant(
        participant,
        currentParticipant,
        currentDashboardUserId
      )
  );
  const otherParticipantWithImage = otherParticipants.find((participant) =>
    getProfileImageUrl(participant)
  );

  return (
    otherParticipantWithImage ?? otherParticipants[0] ?? participants[0] ?? null
  );
};

const getSupportChannelAvatarParticipant = (
  channel: CustomChatChannel
): CustomChatParticipant | null => {
  const preferredParticipantType: CustomChatParticipantType | null =
    channel.type === 'clientSupport'
      ? 'client'
      : channel.type === 'cleanerSupport'
      ? 'cleaner'
      : null;

  if (!preferredParticipantType) return null;

  const participants = channel.participants ?? [];
  const preferredParticipants = participants.filter(
    (participant) => participant.participant_type === preferredParticipantType
  );
  const otherExternalParticipants = participants.filter(
    (participant) => participant.participant_type !== 'dashboard_user'
  );

  return (
    preferredParticipants.find((participant) =>
      getProfileImageUrl(participant)
    ) ??
    preferredParticipants[0] ??
    otherExternalParticipants.find((participant) =>
      getProfileImageUrl(participant)
    ) ??
    otherExternalParticipants[0] ??
    null
  );
};

const isPersonSupportChannel = (channel: CustomChatChannel) =>
  channel.type === 'clientSupport' || channel.type === 'cleanerSupport';

const isAssignedSupportChannel = (channel: CustomChatChannel) =>
  isPersonSupportChannel(channel) &&
  Boolean(channel.assigned_dashboard_user_id);

const isSupportChannelAssignedToCurrentUser = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null
) => {
  if (!isPersonSupportChannel(channel)) return false;
  if (typeof channel.is_assigned_to_current_user === 'boolean') {
    return channel.is_assigned_to_current_user;
  }

  return Boolean(
    currentDashboardUserId &&
      channel.assigned_dashboard_user_id &&
      Number(channel.assigned_dashboard_user_id) === currentDashboardUserId
  );
};

const getSupportAssignmentName = (channel: CustomChatChannel) => {
  const assignedName = channel.assigned_dashboard_user_name?.trim();
  if (assignedName) return assignedName;
  if (channel.assigned_dashboard_user_id) {
    return `Dashboard user #${channel.assigned_dashboard_user_id}`;
  }
  return null;
};

const getSupportAssignmentLabel = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null
) => {
  if (!isPersonSupportChannel(channel)) return null;
  if (!isAssignedSupportChannel(channel)) return 'Unassigned';
  if (isSupportChannelAssignedToCurrentUser(channel, currentDashboardUserId)) {
    return 'Assigned to you';
  }

  const assignedName = getSupportAssignmentName(channel);
  return assignedName ? `Assigned to ${assignedName}` : 'Assigned';
};

const getSupportAssignmentChipColor = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null
) => {
  if (!isAssignedSupportChannel(channel)) return 'warning';
  return isSupportChannelAssignedToCurrentUser(channel, currentDashboardUserId)
    ? 'success'
    : 'default';
};

const canHandleSupportChannel = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null,
  isSuperAdmin?: boolean
) =>
  !isPersonSupportChannel(channel) ||
  Boolean(isSuperAdmin) ||
  isSupportChannelAssignedToCurrentUser(channel, currentDashboardUserId);

const canTransferSupportChannel = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null,
  isSuperAdmin?: boolean
) =>
  isAssignedSupportChannel(channel) &&
  (Boolean(isSuperAdmin) ||
    isSupportChannelAssignedToCurrentUser(channel, currentDashboardUserId));

const getSupportComposerDisabledReason = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null
) => {
  if (!isPersonSupportChannel(channel)) return null;
  if (!isAssignedSupportChannel(channel)) {
    return 'Pick up this chat before replying.';
  }

  const assignedName = getSupportAssignmentName(channel);
  return assignedName
    ? `This chat is assigned to ${assignedName}.`
    : 'This chat is assigned to another dashboard user.';
};

const getDisplayChannelTitle = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null
) =>
  channel.type === 'directMessage'
    ? getDirectMessageTitle(channel, currentDashboardUserId)
    : getChannelTitle(channel);

const isActiveDashboardUser = (dashboardUser: User) => {
  const statusLabels = [
    dashboardUser.user_statuses?.display,
    dashboardUser.user_statuses?.value,
  ].map((statusLabel) => statusLabel?.trim().toLowerCase());

  return dashboardUser.status === 1 || statusLabels.includes('active');
};

const getSenderKey = (
  message: Pick<CustomChatMessage, 'sender_id' | 'sender_type'>
) => `${message.sender_type}:${message.sender_id}`;

const isGeneratedSenderName = (
  message: CustomChatMessage,
  senderName: string
) => {
  const normalizedSenderName = senderName.replace(/[_\s]+/g, ' ').trim();
  const normalizedFallback = `${message.sender_type} ${message.sender_id}`
    .replace(/[_\s]+/g, ' ')
    .trim();

  return normalizedSenderName === normalizedFallback;
};

const getMessageSenderName = (
  message: CustomChatMessage | undefined,
  senderNameByKey: Map<string, string>,
  fallbackSenderName: string
) => {
  if (!message) return fallbackSenderName;

  const participantName = senderNameByKey.get(getSenderKey(message));
  if (participantName) return participantName;

  const apiSenderName = message.sender_name?.replace(/\s+/g, ' ').trim();
  if (apiSenderName && !isGeneratedSenderName(message, apiSenderName)) {
    return apiSenderName;
  }

  const senderName = getPersonDisplayName(message.sender);
  if (senderName) return senderName;

  return fallbackSenderName;
};

const getParticipantKey = (
  participant: Pick<
    CustomChatParticipant,
    'participant_id' | 'participant_type'
  >
) => `${participant.participant_type}:${participant.participant_id}`;

const getSearchableMetadataText = (
  metadata?: Record<string, unknown> | null
): string => {
  if (!metadata) return '';

  return Object.values(metadata)
    .map((value) => {
      if (value === undefined || value === null) return '';
      if (typeof value === 'object') {
        return Object.values(value as Record<string, unknown>).join(' ');
      }
      return String(value);
    })
    .join(' ');
};

const getChannelSearchText = (
  channel: CustomChatChannel,
  currentDashboardUserId: number | null
) =>
  [
    getDisplayChannelTitle(channel, currentDashboardUserId),
    CHANNEL_TYPE_LABELS[channel.type],
    getLastMessageText(channel.last_message),
    getSearchableMetadataText(channel.metadata),
    ...(channel.participants ?? []).map((participant) =>
      [
        getPersonDisplayName(participant),
        participant.email,
        participant.participant_id,
        participant.participant_type,
      ]
        .filter(Boolean)
        .join(' ')
    ),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const getMemberStatus = (
  participants: CustomChatChannel['participants'],
  currentDashboardUserId: number | null,
  senderNameByKey: Map<string, string>
) => {
  const otherParticipants = (participants ?? [])
    .filter(
      (participant) =>
        !(
          participant.participant_type === 'dashboard_user' &&
          Number(participant.participant_id) === currentDashboardUserId
        )
    )
    .map((participant) => ({
      participant,
      readAt: getTimestamp(participant.last_read_at),
    }))
    .sort(
      (firstParticipant, secondParticipant) =>
        (secondParticipant.readAt ?? 0) - (firstParticipant.readAt ?? 0)
    );

  const latestParticipant = otherParticipants[0];
  if (!latestParticipant) return null;

  const participantName =
    senderNameByKey.get(getParticipantKey(latestParticipant.participant)) ??
    getPersonDisplayName(latestParticipant.participant) ??
    'Member';

  if (!latestParticipant.readAt) {
    return {
      color: 'text.disabled',
      label: `${participantName} not seen yet`,
      title: 'No read activity yet',
    };
  }

  const lastReadAt = latestParticipant.participant.last_read_at as string;
  const isActiveRecently =
    Date.now() - latestParticipant.readAt <= ACTIVE_RECENTLY_THRESHOLD_MS;

  return {
    color: isActiveRecently ? 'success.main' : 'text.disabled',
    label: isActiveRecently
      ? `${participantName} active recently`
      : `${participantName} seen ${fToNow(lastReadAt)}`,
    title: `Last active ${formatDate(lastReadAt)}`,
  };
};

const getParticipantStatus = (participant: CustomChatParticipant) => {
  const readAt = getTimestamp(participant.last_read_at);

  if (!readAt) {
    return {
      color: 'text.disabled',
      label: 'Not seen yet',
      readLabel: 'No read activity',
      title: 'No read activity yet',
    };
  }

  const isActiveRecently = Date.now() - readAt <= ACTIVE_RECENTLY_THRESHOLD_MS;
  const lastReadAt = participant.last_read_at as string;

  return {
    color: isActiveRecently ? 'success.main' : 'text.disabled',
    label: isActiveRecently ? 'Active recently' : `Seen ${fToNow(lastReadAt)}`,
    readLabel: `Last read ${formatDate(lastReadAt)}`,
    title: `Last active ${formatDate(lastReadAt)}`,
  };
};

const getParticipantSourceLabel = (participant: CustomChatParticipant) => {
  if (!participant.source_access_group_id) return 'Direct';
  if (participant.source_access_group_name) {
    return `Access group: ${participant.source_access_group_name}`;
  }
  return `Access group #${participant.source_access_group_id}`;
};

const isCurrentDashboardParticipant = (
  participant: CustomChatParticipant,
  currentDashboardUserId: number | null
) =>
  participant.participant_type === 'dashboard_user' &&
  Number(participant.participant_id) === currentDashboardUserId;

const getReadIndicator = (
  message: CustomChatMessage,
  participants: CustomChatChannel['participants'],
  senderNameByKey: Map<string, string>
) => {
  const messageTimestamp = getTimestamp(message.created_at);
  if (!messageTimestamp) {
    return {
      icon: 'eva:checkmark-fill',
      isRead: false,
      label: 'Sent',
      title: 'Read status unavailable',
    };
  }

  const readers = (participants ?? []).filter((participant) => {
    if (
      participant.participant_type === message.sender_type &&
      Number(participant.participant_id) === Number(message.sender_id)
    ) {
      return false;
    }

    const readTimestamp = getTimestamp(participant.last_read_at);
    return readTimestamp !== null && readTimestamp >= messageTimestamp;
  });

  if (!readers.length) {
    return {
      icon: 'eva:checkmark-fill',
      isRead: false,
      label: 'Sent',
      title: 'Not read yet',
    };
  }

  const readerNames = readers
    .map((reader) => {
      const readerKey = `${reader.participant_type}:${reader.participant_id}`;
      return senderNameByKey.get(readerKey) ?? getPersonDisplayName(reader);
    })
    .filter(Boolean);

  return {
    icon: 'eva:done-all-fill',
    isRead: true,
    label: 'Read',
    title: readerNames.length ? `Read by ${readerNames.join(', ')}` : 'Read',
  };
};

const ChannelListItem = ({
  channel,
  currentDashboardUserId,
  isActive,
  onSelect,
}: {
  channel: CustomChatChannel;
  currentDashboardUserId?: number | null;
  isActive: boolean;
  onSelect: (channelId: number | string) => void;
}) => {
  const title = getChannelTitle(channel);
  const avatarParticipant = getSupportChannelAvatarParticipant(channel);
  const showAvatar = isPersonSupportChannel(channel);
  const avatarName = avatarParticipant
    ? getPersonDisplayName(avatarParticipant) ?? title
    : title;
  const assignmentLabel = getSupportAssignmentLabel(
    channel,
    currentDashboardUserId ?? null
  );

  return (
    <ListItemButton
      selected={isActive}
      onClick={() => onSelect(channel.id)}
      sx={{ alignItems: 'flex-start', borderRadius: 1, mx: 1, my: 0.5 }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{ width: 1 }}
      >
        {showAvatar && (
          <Avatar
            src={
              avatarParticipant
                ? getProfileImageUrl(avatarParticipant)
                : undefined
            }
            sx={{
              flexShrink: 0,
              fontSize: 12,
              height: 34,
              mt: 0.25,
              width: 34,
            }}
          >
            {avatarName.slice(0, 2).toUpperCase()}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
              {title}
            </Typography>
            {!!channel.unread_count && (
              <Chip
                color="error"
                label={channel.unread_count}
                size="small"
                sx={{ height: 20, minWidth: 20 }}
              />
            )}
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {!isPersonSupportChannel(channel) && (
              <Typography variant="caption" color="text.secondary">
                {CHANNEL_TYPE_LABELS[channel.type]}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" noWrap>
              {getLastMessageText(channel.last_message)}
            </Typography>
            {assignmentLabel && (
              <Chip
                color={getSupportAssignmentChipColor(
                  channel,
                  currentDashboardUserId ?? null
                )}
                label={assignmentLabel}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  height: 22,
                  maxWidth: 1,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
                variant={
                  isAssignedSupportChannel(channel) ? 'outlined' : 'filled'
                }
              />
            )}
          </Stack>
        </Box>
      </Stack>
    </ListItemButton>
  );
};

const ChatNavItem = ({
  active,
  count,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  count?: number;
  icon: string;
  label: string;
  onClick: () => void;
}) => (
  <ListItemButton
    selected={active}
    onClick={onClick}
    sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: 1 }}>
      <Iconify icon={icon} width={22} />
      <Typography variant="subtitle2" sx={{ flex: 1 }}>
        {label}
      </Typography>
      {typeof count === 'number' && count > 0 && (
        <Chip label={count} size="small" sx={{ height: 20 }} />
      )}
    </Stack>
  </ListItemButton>
);

const DirectMessageListItem = ({
  channel,
  currentDashboardUserId,
  isActive,
  onSelect,
}: {
  channel: CustomChatChannel;
  currentDashboardUserId: number | null;
  isActive: boolean;
  onSelect: (channelId: number | string) => void;
}) => {
  const title = getDirectMessageTitle(channel, currentDashboardUserId);
  const currentParticipant = getCurrentChannelParticipant(
    channel,
    currentDashboardUserId
  );
  const otherParticipants = (channel.participants ?? []).filter(
    (participant) =>
      !isCurrentChannelParticipant(
        participant,
        currentParticipant,
        currentDashboardUserId
      )
  );
  const avatarParticipants = otherParticipants.length
    ? otherParticipants
    : channel.participants ?? [];
  const visibleAvatarParticipants = avatarParticipants.slice(0, 3);
  const remainingAvatarCount = Math.max(avatarParticipants.length - 3, 0);

  return (
    <ListItemButton
      selected={isActive}
      onClick={() => onSelect(channel.id)}
      sx={{ alignItems: 'flex-start', borderRadius: 1, mx: 1, my: 0.5 }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            flexShrink: 0,
            minWidth: remainingAvatarCount
              ? 34 + visibleAvatarParticipants.length * 14
              : visibleAvatarParticipants.length > 1
              ? 34 + (visibleAvatarParticipants.length - 1) * 14
              : 34,
            mt: 0.5,
          }}
        >
          {visibleAvatarParticipants.map((participant, index) => {
            const participantName = getPersonDisplayName(participant) ?? title;

            return (
              <Avatar
                key={`${participant.participant_type}:${participant.participant_id}`}
                src={getProfileImageUrl(participant)}
                sx={{
                  border: 2,
                  borderColor: 'background.paper',
                  fontSize: 12,
                  height: 34,
                  ml: index === 0 ? 0 : -1.25,
                  width: 34,
                  zIndex: visibleAvatarParticipants.length - index,
                }}
              >
                {participantName.slice(0, 2).toUpperCase()}
              </Avatar>
            );
          })}
          {!visibleAvatarParticipants.length && (
            <Avatar sx={{ width: 34, height: 34, fontSize: 13 }}>
              {title.slice(0, 2).toUpperCase()}
            </Avatar>
          )}
          {remainingAvatarCount > 0 && (
            <Avatar
              sx={{
                bgcolor: 'common.white',
                border: 2,
                borderColor: 'grey.200',
                color: 'text.primary',
                fontSize: 12,
                fontWeight: 700,
                height: 34,
                ml: -1.25,
                width: 34,
                zIndex: 0,
              }}
            >
              +{remainingAvatarCount}
            </Avatar>
          )}
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
              {title}
            </Typography>
            {!!channel.unread_count && (
              <Chip
                color="error"
                label={channel.unread_count}
                size="small"
                sx={{ height: 20, minWidth: 20 }}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap>
            {getLastMessageText(channel.last_message)}
          </Typography>
        </Box>
      </Stack>
    </ListItemButton>
  );
};

const CustomChatJobSupportGroupSection = ({
  activeChannelId,
  group,
  items,
  onSelect,
}: {
  activeChannelId?: number | string | null;
  group: (typeof CUSTOM_CHAT_JOB_SUPPORT_GROUPS)[number];
  items: GroupedCustomChatJobSupportChannel[];
  onSelect: (channelId: number | string) => void;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(
    Boolean(group.defaultCollapsed)
  );

  if (!items.length) return null;

  return (
    <Box sx={{ px: 1 }}>
      <Box
        component="button"
        type="button"
        aria-expanded={!isCollapsed}
        onClick={() => setIsCollapsed((current) => !current)}
        sx={{
          alignItems: 'center',
          bgcolor: 'grey.100',
          border: 0,
          borderRadius: 1,
          color: 'text.primary',
          cursor: 'pointer',
          display: 'flex',
          gap: 1,
          minHeight: 44,
          px: 1.25,
          py: 0.75,
          textAlign: 'left',
          width: 1,
          '&:hover': {
            bgcolor: 'grey.200',
          },
        }}
      >
        <Iconify
          icon={
            isCollapsed ? 'eva:chevron-right-fill' : 'eva:chevron-down-fill'
          }
          width={18}
        />
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
          {group.label}
        </Typography>
        <Chip
          label={items.length}
          size="small"
          sx={{
            bgcolor: 'grey.300',
            fontWeight: 700,
            height: 24,
            minWidth: 24,
          }}
        />
      </Box>
      {!isCollapsed && (
        <List disablePadding sx={{ mt: 0.5 }}>
          {items.map(({ channel }) => (
            <ChannelListItem
              key={channel.id}
              channel={channel}
              isActive={channel.id === activeChannelId}
              onSelect={onSelect}
            />
          ))}
        </List>
      )}
    </Box>
  );
};

const CustomChatJobSupportGroups = ({
  activeChannelId,
  channels,
  onSelect,
  sessionsById,
}: {
  activeChannelId?: number | string | null;
  channels: CustomChatChannel[];
  onSelect: (channelId: number | string) => void;
  sessionsById: Map<number, BookingSession>;
}) => {
  const groupedChannels = useMemo(
    () => groupCustomChatJobSupportChannels(channels, sessionsById),
    [channels, sessionsById]
  );

  return (
    <Stack spacing={1}>
      {CUSTOM_CHAT_JOB_SUPPORT_GROUPS.map((group) => (
        <CustomChatJobSupportGroupSection
          key={group.id}
          activeChannelId={activeChannelId}
          group={group}
          items={sortCustomChatJobSupportGroupItems(
            groupedChannels[group.id],
            group.sort
          )}
          onSelect={onSelect}
        />
      ))}
    </Stack>
  );
};

const JobMetadata = ({ channel }: { channel?: CustomChatChannel | null }) => {
  const metadata = channel?.metadata ?? {};
  const entries = [
    {
      key: 'booking',
      label: 'Booking',
      value: metadata.bookingId ?? metadata.booking_id,
    },
    {
      key: 'session',
      label: 'Session',
      value: metadata.bookingSessionId ?? metadata.booking_session_id,
    },
    {
      key: 'date',
      label: 'Date',
      value: metadata.bookingDate ?? metadata.date,
    },
    {
      key: 'time',
      label: 'Time',
      value: metadata.bookingTime ?? metadata.time,
    },
    {
      key: 'status',
      label: 'Status',
      value: metadata.status ?? metadata.bookingStatus,
    },
  ].filter(
    ({ value }) => value !== undefined && value !== null && value !== ''
  );

  if (!entries.length) return null;

  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
      {entries.map((entry) => (
        <Chip
          key={entry.key}
          size="small"
          variant="outlined"
          label={`${entry.label}: ${String(entry.value)}`}
        />
      ))}
    </Stack>
  );
};

const CustomChatJobDetailsPanel = ({
  bookingSessionId,
  isError,
  isLoading,
  links,
  metadata,
  session,
}: {
  bookingSessionId: number | null;
  isError: boolean;
  isLoading: boolean;
  links: CustomChatJobContextLinks | null;
  metadata: Record<string, unknown>;
  session?: BookingSession;
}) => {
  if (isLoading) {
    return (
      <Box
        component="aside"
        aria-label="Loading job details"
        sx={{
          borderLeft: 1,
          borderColor: 'divider',
          flexShrink: 0,
          overflow: 'auto',
          p: 3,
          width: 360,
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 700 }}
        >
          Job details
        </Typography>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Loading context...
        </Typography>
        <Stack spacing={1.5}>
          {[0, 1, 2, 3].map((item) => (
            <Box
              key={item}
              sx={{
                bgcolor: 'grey.100',
                borderRadius: 1,
                height: item === 1 ? 44 : 64,
              }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  if (isError || !session) {
    const fallbackSessionLabel =
      bookingSessionId ??
      getMetadataString(metadata, ['bookingSessionId', 'booking_session_id']);

    return (
      <Box
        component="aside"
        aria-label="Job details"
        sx={{
          borderLeft: 1,
          borderColor: 'divider',
          flexShrink: 0,
          overflow: 'auto',
          p: 3,
          width: 360,
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 700 }}
        >
          Job details
        </Typography>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Session #{fallbackSessionLabel}
        </Typography>
        <Alert severity={isError ? 'error' : 'info'}>
          {isError
            ? 'Unable to load session context.'
            : 'No session context found.'}
        </Alert>
      </Box>
    );
  }

  const rows = getJobDetailRows(session);
  const notes = getJobNotes(session);
  const statusTone = getStatusTone(session.booking_session_status);
  const paymentTone = getStatusTone(session.booking_session_payment_status);

  return (
    <Box
      component="aside"
      aria-label="Job details"
      sx={{
        borderLeft: 1,
        borderColor: 'divider',
        flexShrink: 0,
        overflow: 'auto',
        p: 3,
        width: 360,
      }}
    >
      <Stack spacing={2.25}>
        <Box>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontWeight: 700 }}
          >
            Job details
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Session #{session.id}
          </Typography>
        </Box>

        {links && (
          <Stack direction="row" spacing={1}>
            <Button
              component={RouterLink}
              to={links.bookingPath}
              variant="outlined"
              startIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
              sx={{
                borderRadius: 1,
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              Open Booking
            </Button>
            {links.sessionPath && (
              <Button
                component={RouterLink}
                to={links.sessionPath}
                variant="contained"
                startIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
                sx={{
                  bgcolor: '#244B73',
                  borderRadius: 1,
                  boxShadow: 'none',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#1E4164',
                    boxShadow: 'none',
                  },
                }}
              >
                Open Session
              </Button>
            )}
          </Stack>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            color={getChipColor(statusTone)}
            label={session.booking_session_status?.display ?? EMPTY_JOB_VALUE}
            sx={{ fontWeight: 700 }}
            variant={statusTone === 'default' ? 'outlined' : 'filled'}
          />
          <Chip
            color={getChipColor(paymentTone)}
            label={
              session.booking_session_payment_status?.display ?? EMPTY_JOB_VALUE
            }
            sx={{ fontWeight: 700 }}
            variant={paymentTone === 'default' ? 'outlined' : 'filled'}
          />
        </Stack>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: 'text.secondary', fontWeight: 800, mb: 1.25 }}
          >
            OPERATIONAL SUMMARY
          </Typography>
          <Stack
            divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}
          >
            {rows.map(({ label, value }) => (
              <Stack key={label} direction="row" spacing={2} sx={{ py: 1.25 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    flexShrink: 0,
                    fontWeight: 700,
                    width: 120,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.primary',
                    minWidth: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        {notes.length > 0 && (
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: 'text.secondary', fontWeight: 800, mb: 1.25 }}
            >
              NOTES
            </Typography>
            <Stack
              divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}
            >
              {notes.map(({ label, value }) => (
                <Box key={label} sx={{ py: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontWeight: 700, mb: 0.5 }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

const MemberStatusIndicator = ({
  status,
}: {
  status: ReturnType<typeof getMemberStatus>;
}) => {
  if (!status) return null;

  return (
    <Tooltip title={status.title}>
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ mt: 0.5, width: 'fit-content' }}
      >
        <Box
          sx={{
            bgcolor: status.color,
            borderRadius: '50%',
            height: 8,
            width: 8,
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {status.label}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const ParticipantsDialog = ({
  addableUsers,
  canManageParticipants,
  currentDashboardUserId,
  isAddingParticipants,
  isLoadingUsers,
  isRemovingParticipant,
  onClose,
  onAddParticipants,
  open,
  participants,
  removingParticipantKey,
  onRemoveParticipant,
}: {
  addableUsers: User[];
  canManageParticipants: boolean;
  currentDashboardUserId: number | null;
  isAddingParticipants: boolean;
  isLoadingUsers: boolean;
  isRemovingParticipant: boolean;
  onClose: () => void;
  onAddParticipants: (users: User[]) => void;
  onRemoveParticipant: (participant: CustomChatParticipantIdentity) => void;
  open: boolean;
  participants: CustomChatChannel['participants'];
  removingParticipantKey?: string | null;
}) => {
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  useEffect(() => {
    if (!open) setSelectedUsers([]);
  }, [open]);

  const handleAddParticipants = () => {
    if (!selectedUsers.length) return;
    onAddParticipants(selectedUsers);
    setSelectedUsers([]);
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>Participants</DialogTitle>
      <DialogContent>
        {canManageParticipants && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            sx={{ pb: 1.5 }}
          >
            <Autocomplete
              multiple
              options={addableUsers}
              loading={isLoadingUsers}
              value={selectedUsers}
              onChange={(_event, value) => setSelectedUsers(value)}
              getOptionLabel={getUserOptionLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) => {
                const search = state.inputValue.trim().toLowerCase();
                if (!search) return options;

                return options.filter((option) =>
                  [
                    option.id,
                    option.first_name,
                    option.last_name,
                    option.email,
                    option.phone,
                  ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                    .includes(search)
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add dashboard users"
                  placeholder="Search users"
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack spacing={0.25}>
                    <Typography variant="body2">
                      {getUserOptionLabel(option)}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )}
              sx={{ flex: 1 }}
            />
            <Button
              disabled={!selectedUsers.length || isAddingParticipants}
              onClick={handleAddParticipants}
              variant="contained"
              sx={{ minWidth: 88 }}
            >
              {isAddingParticipants ? 'Adding...' : 'Add'}
            </Button>
          </Stack>
        )}

        {participants?.length ? (
          <Stack spacing={0.75} sx={{ py: 0.5 }}>
            {participants.map((participant) => {
              const participantName =
                getPersonDisplayName(participant) ||
                `${
                  CUSTOM_CHAT_PARTICIPANT_ROLE_LABELS[
                    participant.participant_type
                  ]
                } ${participant.participant_id}`;
              const participantRoleLabel =
                CUSTOM_CHAT_PARTICIPANT_ROLE_LABELS[
                  participant.participant_type
                ];
              const participantStatus = getParticipantStatus(participant);
              const isCurrentParticipant = isCurrentDashboardParticipant(
                participant,
                currentDashboardUserId
              );
              const participantKey = `${participant.participant_type}:${participant.participant_id}`;
              const canRemoveParticipant =
                canManageParticipants &&
                !isCurrentParticipant &&
                participant.participant_type === 'dashboard_user';

              return (
                <Box
                  key={participantKey}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      src={getProfileImageUrl(participant)}
                      sx={{ width: 34, height: 34, fontSize: 12 }}
                    >
                      {participantName.slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Typography variant="subtitle2" noWrap>
                          {participantName}
                        </Typography>
                        {isCurrentParticipant && (
                          <Chip label="You" size="small" variant="outlined" />
                        )}
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={0.35}
                        alignItems="center"
                        sx={{ color: 'text.secondary' }}
                      >
                        {participant.participant_type === 'client' && (
                          <Iconify
                            icon="eva:star-fill"
                            width={12}
                            aria-hidden
                            sx={{ color: 'warning.main', flexShrink: 0 }}
                          />
                        )}
                        <Typography variant="caption">
                          {participantRoleLabel}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ mt: 0.5 }}
                      >
                        <Tooltip title={participantStatus.title}>
                          <Box
                            sx={{
                              bgcolor: participantStatus.color,
                              borderRadius: '50%',
                              height: 8,
                              width: 8,
                            }}
                          />
                        </Tooltip>
                        <Typography variant="body2">
                          {participantStatus.label}
                        </Typography>
                        <Chip
                          label={getParticipantSourceLabel(participant)}
                          size="small"
                          sx={{ height: 22 }}
                        />
                        {isCurrentParticipant && participant.hidden_at && (
                          <Chip
                            label="Archived"
                            size="small"
                            color="warning"
                            sx={{ height: 22 }}
                          />
                        )}
                        {isCurrentParticipant && participant.muted_at && (
                          <Chip
                            label="Muted"
                            size="small"
                            color="default"
                            sx={{ height: 22 }}
                          />
                        )}
                      </Stack>
                    </Box>
                    {canRemoveParticipant && (
                      <Tooltip title="Remove participant">
                        <span>
                          <IconButton
                            color="error"
                            disabled={
                              isRemovingParticipant &&
                              removingParticipantKey === participantKey
                            }
                            size="small"
                            onClick={() =>
                              onRemoveParticipant({
                                participant_id: Number(
                                  participant.participant_id
                                ),
                                participant_type: participant.participant_type,
                              })
                            }
                          >
                            {isRemovingParticipant &&
                            removingParticipantKey === participantKey ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : (
                              <Iconify icon="eva:trash-2-outline" width={18} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No participants found.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const MessageAttachments = ({
  attachments,
  message,
  onOpenImageGallery,
}: {
  attachments: CustomChatAttachment[];
  message: CustomChatMessage;
  onOpenImageGallery: (image: SelectedChatGalleryImage) => void;
}) => {
  const accessUrlQueries = useQueries({
    queries: attachments.map((attachment) => ({
      enabled: Boolean(message.channel_id && attachment.id),
      queryFn: () =>
        getCustomChatAttachmentAccessUrl(
          message.channel_id,
          attachment.id as number | string
        ),
      queryKey: [
        'customChat',
        'attachmentAccessUrl',
        message.channel_id,
        attachment.id,
      ],
      staleTime: ATTACHMENT_ACCESS_URL_STALE_TIME_MS,
    })),
  });
  const attachmentViews = attachments.map((attachment, index) => {
    const signedUrl = (
      accessUrlQueries[index]?.data as { url?: unknown } | undefined
    )?.url;
    const accessUrl =
      typeof signedUrl === 'string' && isSafeAttachmentUrl(signedUrl)
        ? signedUrl
        : null;

    return {
      accessUrl,
      attachment,
      galleryKey: getChatImageGalleryKey(message, attachment, index),
      index,
      isError: Boolean(accessUrlQueries[index]?.isError),
      isImage: isImageAttachment(attachment),
      isLoading: Boolean(accessUrlQueries[index]?.isLoading),
      label: attachment.name || `Attachment ${index + 1}`,
    };
  });
  const imageViews = attachmentViews.filter(
    (attachmentView) => attachmentView.isImage && attachmentView.accessUrl
  );
  const fileViews = attachmentViews.filter(
    (attachmentView) => !attachmentView.isImage
  );
  const unavailableImageViews = attachmentViews.filter(
    (attachmentView) =>
      attachmentView.isImage &&
      (!attachmentView.accessUrl ||
        attachmentView.isLoading ||
        attachmentView.isError)
  );

  return (
    <Stack spacing={0.75}>
      {!!imageViews.length && (
        <Box
          sx={{
            display: 'grid',
            gap: 0.75,
            gridTemplateColumns:
              imageViews.length === 1
                ? 'minmax(0, 1fr)'
                : 'repeat(2, minmax(0, 1fr))',
            maxWidth: imageViews.length === 1 ? 320 : 360,
          }}
        >
          {imageViews.map((attachmentView) => (
            <Box
              key={attachmentView.galleryKey}
              component="button"
              type="button"
              onClick={() =>
                onOpenImageGallery({
                  accessUrl: attachmentView.accessUrl as string,
                  key: attachmentView.galleryKey,
                })
              }
              sx={{
                aspectRatio: imageViews.length === 1 ? '4 / 3' : '1 / 1',
                bgcolor: 'background.paper',
                border: 0,
                borderRadius: 1,
                cursor: 'pointer',
                lineHeight: 0,
                overflow: 'hidden',
                p: 0,
              }}
            >
              <Box
                component="img"
                src={attachmentView.accessUrl as string}
                alt={attachmentView.label}
                loading="lazy"
                sx={{
                  display: 'block',
                  height: '100%',
                  objectFit: 'cover',
                  width: '100%',
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      {[...fileViews, ...unavailableImageViews].map((attachmentView) => {
        if (attachmentView.isLoading) {
          return (
            <Stack
              key={`${attachmentView.attachment.id}-${attachmentView.index}`}
              direction="row"
              spacing={0.75}
              alignItems="center"
            >
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">
                Loading attachment
              </Typography>
            </Stack>
          );
        }

        if (attachmentView.accessUrl) {
          return (
            <Link
              key={`${attachmentView.attachment.id}-${attachmentView.index}`}
              href={attachmentView.accessUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ wordBreak: 'break-word' }}
            >
              {attachmentView.label}
            </Link>
          );
        }

        return (
          <Typography
            key={`${attachmentView.attachment.id}-${attachmentView.index}`}
            variant="caption"
            color="text.secondary"
          >
            {attachmentView.label}
          </Typography>
        );
      })}
    </Stack>
  );
};

const MessageDateSeparator = ({ label }: { label: string }) => (
  <Stack alignItems="center" sx={{ py: 1 }}>
    <Box
      sx={{
        bgcolor: 'grey.100',
        borderRadius: 999,
        px: 2,
        py: 0.75,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 600 }}
      >
        {label}
      </Typography>
    </Box>
  </Stack>
);

const MessageTypingIndicator = ({ senderName }: { senderName: string }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 1 }}>
    <Avatar sx={{ width: 30, height: 30, fontSize: 12 }}>
      {senderName.slice(0, 2).toUpperCase()}
    </Avatar>
    <Stack direction="row" spacing={0.75} aria-label={`${senderName} typing`}>
      {[0, 1, 2].map((dotIndex) => (
        <Box
          key={dotIndex}
          sx={{
            bgcolor: dotIndex === 2 ? 'grey.400' : 'grey.300',
            borderRadius: '50%',
            height: 8,
            width: 8,
          }}
        />
      ))}
    </Stack>
  </Stack>
);

const getMessageBubbleRadius = (
  isOwnMessage: boolean,
  isLastMessage: boolean
) => {
  if (!isLastMessage) return '22px';

  return isOwnMessage ? '22px 22px 6px 22px' : '22px 22px 22px 6px';
};

const GeneralMessageNotice = ({ message }: { message: CustomChatMessage }) => {
  const text = message.deleted_at
    ? 'Notice deleted'
    : getCustomChatMessageDisplayText(message);

  if (!text.trim()) return null;

  return (
    <Stack alignItems="center" sx={{ width: 1 }}>
      <Box
        sx={{
          bgcolor: 'grey.100',
          borderRadius: 999,
          maxWidth: { xs: '92%', sm: '74%' },
          px: 2,
          py: 1,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {renderMessageText(text, message.mentions, message.links)}
        </Typography>
        <Typography
          component="div"
          variant="caption"
          color="text.disabled"
          sx={{ mt: 0.25 }}
        >
          {formatMessageTime(message.created_at)}
        </Typography>
      </Box>
    </Stack>
  );
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderMessageText = (
  text: string,
  mentions?: CustomChatMention[] | null,
  links?: CustomChatLink[] | null
) => {
  const validMentions = (mentions ?? []).filter(
    (mention) => mention.display_name?.trim()
  );
  const validLinks = [...(links ?? [])]
    .filter(
      (link) =>
        Number.isInteger(link.start) &&
        Number.isInteger(link.end) &&
        link.start >= 0 &&
        link.end > link.start &&
        link.end <= text.length &&
        isSafeAttachmentUrl(link.url)
    )
    .sort((firstLink, secondLink) => firstLink.start - secondLink.start)
    .reduce<CustomChatLink[]>((dedupedLinks, link) => {
      const previousLink = dedupedLinks[dedupedLinks.length - 1];

      if (previousLink && link.start < previousLink.end) {
        return dedupedLinks;
      }

      dedupedLinks.push(link);
      return dedupedLinks;
    }, []);

  const renderMentions = (value: string, keyPrefix: string) => {
    if (!validMentions.length) return value;

    const mentionPattern = new RegExp(
      `@(${validMentions
        .map((mention) => escapeRegExp(mention.display_name.trim()))
        .join('|')})`,
      'g'
    );
    const parts: Array<{ isMention: boolean; text: string }> = [];
    let lastIndex = 0;
    let match = mentionPattern.exec(value);

    while (match) {
      if (match.index > lastIndex) {
        parts.push({
          isMention: false,
          text: value.slice(lastIndex, match.index),
        });
      }

      parts.push({ isMention: true, text: match[0] });
      lastIndex = match.index + match[0].length;
      match = mentionPattern.exec(value);
    }

    if (lastIndex < value.length) {
      parts.push({ isMention: false, text: value.slice(lastIndex) });
    }

    return parts.map((part, index) =>
      part.isMention ? (
        <Box
          key={`${keyPrefix}-${part.text}-${index}`}
          component="span"
          sx={{
            bgcolor: 'rgba(33, 150, 243, 0.14)',
            borderRadius: 0.75,
            color: 'primary.dark',
            fontWeight: 700,
            px: 0.4,
          }}
        >
          {part.text}
        </Box>
      ) : (
        part.text
      )
    );
  };

  if (!validLinks.length) return renderMentions(text, 'message-text');

  const parts: Array<{
    content: JSX.Element | ReturnType<typeof renderMentions>;
    key: string;
  }> = [];
  let lastIndex = 0;

  validLinks.forEach((link, index) => {
    if (link.start > lastIndex) {
      parts.push({
        content: renderMentions(
          text.slice(lastIndex, link.start),
          `text-${index}`
        ),
        key: `text-${lastIndex}-${link.start}`,
      });
    }

    parts.push({
      content: (
        <Link
          key={`link-${link.start}-${link.end}`}
          href={link.url}
          rel="noopener noreferrer"
          target="_blank"
          underline="hover"
          sx={{
            color: 'primary.dark',
            fontWeight: 700,
            overflowWrap: 'anywhere',
          }}
        >
          {text.slice(link.start, link.end)}
        </Link>
      ),
      key: `link-${link.start}-${link.end}`,
    });
    lastIndex = link.end;
  });

  if (lastIndex < text.length) {
    parts.push({
      content: renderMentions(text.slice(lastIndex), `text-${lastIndex}`),
      key: `text-${lastIndex}-${text.length}`,
    });
  }

  return parts.map((part) => <span key={part.key}>{part.content}</span>);
};

const MessageSenderRoleChip = ({ label }: { label: string | null }) => {
  if (!label) return null;

  const isClientRole = label === CUSTOM_CHAT_PARTICIPANT_ROLE_LABELS.client;

  return (
    <Chip
      icon={
        isClientRole ? (
          <Iconify icon="eva:star-fill" width={13} aria-hidden />
        ) : undefined
      }
      label={label}
      size="small"
      variant="outlined"
      sx={{
        borderColor: 'divider',
        color: 'text.secondary',
        fontSize: 11,
        fontWeight: 700,
        height: 20,
        '& .MuiChip-icon': {
          color: 'warning.main',
          ml: 0.75,
          mr: -0.25,
        },
        '& .MuiChip-label': {
          px: 0.75,
        },
      }}
    />
  );
};

const MessageTranslationIndicator = ({
  message,
}: {
  message: CustomChatMessage;
}) => {
  if (message.deleted_at) return null;

  const translationInfo = getCustomChatTranslatedMessageInfo(message);
  if (!translationInfo) return null;

  const originalLabel = translationInfo.sourceLanguage
    ? `Original (${translationInfo.sourceLanguage.toUpperCase()})`
    : 'Original message';

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ maxWidth: 320 }}>
          <Typography variant="caption" fontWeight={700}>
            {originalLabel}
          </Typography>
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {translationInfo.originalText}
          </Typography>
        </Box>
      }
    >
      <Box
        component="span"
        tabIndex={0}
        aria-label={`Translated message. Original: ${translationInfo.originalText}`}
        sx={{
          display: 'inline-flex',
          mt: 0.5,
          px: 1,
        }}
      >
        <Chip
          icon={<Iconify icon="eva:globe-2-outline" width={13} aria-hidden />}
          label="Translated"
          size="small"
          variant="outlined"
          sx={{
            bgcolor: 'background.paper',
            borderColor: 'divider',
            color: 'text.secondary',
            fontSize: 11,
            fontWeight: 700,
            height: 20,
            '& .MuiChip-icon': {
              color: 'info.main',
              ml: 0.75,
              mr: -0.25,
            },
            '& .MuiChip-label': {
              px: 0.75,
            },
          }}
        />
      </Box>
    </Tooltip>
  );
};

const MessageGroup = ({
  group,
  currentParticipant,
  currentDashboardUserId,
  onOpenImageGallery,
  participants,
  senderImageByKey,
  senderNameByKey,
}: {
  group: ReturnType<typeof groupCustomChatMessages>[number];
  currentParticipant: CustomChatParticipant | null;
  currentDashboardUserId: number | null;
  onOpenImageGallery: (image: SelectedChatGalleryImage) => void;
  participants: CustomChatChannel['participants'];
  senderImageByKey: Map<string, string>;
  senderNameByKey: Map<string, string>;
}) => {
  const firstMessage = group.messages[0];
  const isOwnMessage =
    Boolean(firstMessage) &&
    isCurrentDashboardMessage(
      firstMessage,
      currentParticipant,
      currentDashboardUserId
    );
  const senderName = getMessageSenderName(
    firstMessage,
    senderNameByKey,
    group.senderName
  );
  const senderImage = firstMessage
    ? senderImageByKey.get(getSenderKey(firstMessage))
    : undefined;
  const senderRoleLabel = isOwnMessage
    ? 'You'
    : getCustomChatSenderRoleLabel(firstMessage);

  return (
    <Stack
      data-sender-side={isOwnMessage ? 'right' : 'left'}
      direction="row"
      spacing={1}
      alignItems="flex-start"
      justifyContent={isOwnMessage ? 'flex-end' : 'flex-start'}
      sx={{ width: 1 }}
    >
      {!isOwnMessage && (
        <Avatar
          src={senderImage}
          sx={{ width: 34, height: 34, fontSize: 12, mt: 0.5 }}
        >
          {senderName.slice(0, 2).toUpperCase()}
        </Avatar>
      )}
      <Box
        sx={{
          alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: { xs: '86%', sm: '74%' },
          minWidth: 0,
          width: 'fit-content',
        }}
      >
        <Stack
          spacing={0.5}
          sx={{
            alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
            width: '100%',
          }}
        >
          {group.messages.map((message, messageIndex) => {
            const isLastMessage = messageIndex === group.messages.length - 1;
            const isDeleted = Boolean(message.deleted_at);
            const readIndicator =
              isOwnMessage && isLastMessage
                ? getReadIndicator(message, participants, senderNameByKey)
                : null;
            const text = getCustomChatMessageDisplayText(message);
            const hasText = Boolean(text.trim());

            return (
              <Box
                key={message.id}
                sx={{
                  alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '100%',
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    bgcolor: isOwnMessage ? '#F5F0DE' : '#E8DDF7',
                    borderRadius: getMessageBubbleRadius(
                      isOwnMessage,
                      isLastMessage
                    ),
                    color: 'grey.900',
                    px: 1.75,
                    py: 1.25,
                    width: 'fit-content',
                    maxWidth: '100%',
                  }}
                >
                  {isDeleted ? (
                    <Typography variant="body2" color="text.secondary">
                      Message deleted
                    </Typography>
                  ) : (
                    <>
                      {hasText && (
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: 15,
                            lineHeight: 1.45,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {renderMessageText(
                            text,
                            message.mentions,
                            message.links
                          )}
                        </Typography>
                      )}
                      {!!message.attachments?.length && (
                        <Stack spacing={0.5} sx={{ mt: hasText ? 1 : 0 }}>
                          <MessageAttachments
                            attachments={message.attachments}
                            message={message}
                            onOpenImageGallery={onOpenImageGallery}
                          />
                        </Stack>
                      )}
                    </>
                  )}
                </Box>
                <MessageTranslationIndicator message={message} />
                {isLastMessage && (
                  <>
                    {isOwnMessage ? (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexWrap="wrap"
                        justifyContent="flex-end"
                        sx={{ mt: 0.75, px: 1 }}
                      >
                        <MessageSenderRoleChip label={senderRoleLabel} />
                        <Typography variant="caption" color="text.disabled">
                          {formatMessageTime(message.created_at)}
                        </Typography>
                        {readIndicator && (
                          <Tooltip title={readIndicator.title}>
                            <Box
                              aria-label={readIndicator.label}
                              role="img"
                              sx={{
                                bgcolor: readIndicator.isRead
                                  ? 'success.main'
                                  : 'grey.300',
                                borderRadius: '50%',
                                height: 8,
                                width: 8,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ mt: 0.75, px: 1 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {senderName}
                        </Typography>
                        <MessageSenderRoleChip label={senderRoleLabel} />
                        <Typography variant="caption" color="text.disabled">
                          {formatMessageTime(message.created_at)}
                        </Typography>
                      </Stack>
                    )}
                  </>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

const MessageComposer = ({
  channelId,
  currentDashboardUserId,
  currentParticipant,
  disabledReason,
  isDisabled,
  isExternallyLocked,
  participants,
  placeholderTarget,
}: {
  channelId: number | string | null;
  currentDashboardUserId: number | null;
  currentParticipant: CustomChatParticipant | null;
  disabledReason?: string | null;
  isDisabled?: boolean;
  isExternallyLocked?: boolean;
  participants?: CustomChatChannel['participants'];
  placeholderTarget?: string;
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textInputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(
    null
  );
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [mentions, setMentions] = useState<CustomChatMention[]>([]);
  const [selectionStart, setSelectionStart] = useState(0);
  const { errorMsg } = useSnackbarMsg();
  const sendMutation = useSendCustomChatMessageMutation(channelId);
  const mentionCandidates = useMemo(
    () =>
      (participants ?? [])
        .filter(
          (participant) =>
            !isCurrentChannelParticipant(
              participant,
              currentParticipant,
              currentDashboardUserId
            )
        )
        .map((participant) => {
          const displayName = getPersonDisplayName(participant);
          if (!displayName) return null;

          return {
            display_name: displayName,
            participant_id: Number(participant.participant_id),
            participant_type: participant.participant_type,
          };
        })
        .filter(Boolean) as CustomChatMention[],
    [currentDashboardUserId, currentParticipant, participants]
  );

  const isSending = sendMutation.isLoading;
  const isComposerDisabled = Boolean(isExternallyLocked || isDisabled);
  const canSend = Boolean(
    channelId && !isComposerDisabled && (text.trim() || files.length)
  );
  const placeholder = placeholderTarget
    ? `Message ${placeholderTarget}`
    : 'Message';
  const activeMentionMatch = useMemo(() => {
    const textBeforeCursor = text.slice(0, selectionStart);
    const match = textBeforeCursor.match(/(^|\s)@([A-Za-z0-9._-]*)$/);

    if (!match) return null;

    return {
      query: match[2].toLowerCase(),
      startIndex: textBeforeCursor.length - match[2].length - 1,
    };
  }, [selectionStart, text]);
  const mentionOptions = useMemo(() => {
    if (!activeMentionMatch) return [];

    return mentionCandidates
      .filter((mention) =>
        mention.display_name.toLowerCase().includes(activeMentionMatch.query)
      )
      .slice(0, 6);
  }, [activeMentionMatch, mentionCandidates]);
  const showMentionOptions = Boolean(
    activeMentionMatch && mentionOptions.length && !isComposerDisabled
  );

  const updateSelectionStart = (
    input: HTMLInputElement | HTMLTextAreaElement | null
  ) => {
    setSelectionStart(input?.selectionStart ?? text.length);
  };

  const handleSelectMention = (mention: CustomChatMention) => {
    if (!activeMentionMatch) return;

    const mentionText = `@${mention.display_name}`;
    const textBeforeMention = text.slice(0, activeMentionMatch.startIndex);
    const textAfterMention = text.slice(selectionStart);
    const separator = textAfterMention.startsWith(' ') ? '' : ' ';
    const nextText = `${textBeforeMention}${mentionText}${separator}${textAfterMention}`;
    const nextSelectionStart =
      textBeforeMention.length + mentionText.length + separator.length;

    setText(nextText);
    setMentions((currentMentions) => {
      const hasMention = currentMentions.some(
        (currentMention) =>
          currentMention.participant_type === mention.participant_type &&
          currentMention.participant_id === mention.participant_id
      );

      return hasMention ? currentMentions : [...currentMentions, mention];
    });
    setSelectionStart(nextSelectionStart);
    window.setTimeout(() => {
      textInputRef.current?.focus();
      textInputRef.current?.setSelectionRange(
        nextSelectionStart,
        nextSelectionStart
      );
    }, 0);
  };

  const handleSend = async () => {
    if (!channelId || !canSend || isSending || isComposerDisabled) return;
    if (files.length > MAX_CHAT_ATTACHMENTS) {
      errorMsg(
        new Error(`Attach up to ${MAX_CHAT_ATTACHMENTS} files per message.`),
        'Unable to send custom chat message'
      );
      return;
    }

    try {
      const attachments = await Promise.all(files.map(uploadChatFile));
      const activeMentions = mentions.filter((mention) =>
        text.includes(`@${mention.display_name}`)
      );
      await sendMutation.mutateAsync({
        attachments,
        client_message_id: createClientMessageId(),
        ...(activeMentions.length ? { mentions: activeMentions } : {}),
        text: text.trim(),
      });
      setText('');
      setFiles([]);
      setMentions([]);
      setSelectionStart(0);
    } catch (error) {
      errorMsg(error, 'Unable to send custom chat message');
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.paper', p: 2, pt: 1.5 }}>
      {isComposerDisabled && disabledReason && (
        <Alert severity="info" sx={{ mb: 1.25 }}>
          {disabledReason}
        </Alert>
      )}
      {!!files.length && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 1.25 }}>
          {files.map((file) => (
            <Chip
              key={`${file.name}-${file.size}`}
              label={file.name}
              onDelete={() =>
                setFiles((previous) =>
                  previous.filter((candidate) => candidate !== file)
                )
              }
              size="small"
            />
          ))}
        </Stack>
      )}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{
          bgcolor: 'grey.100',
          borderRadius: 2.5,
          minHeight: 64,
          position: 'relative',
          px: 1.25,
          py: 1,
        }}
      >
        <input
          ref={fileInputRef}
          hidden
          type="file"
          multiple
          onChange={(event) => {
            const selectedFiles = Array.from(event.target.files ?? []);

            if (selectedFiles.length > MAX_CHAT_ATTACHMENTS) {
              errorMsg(
                new Error(
                  `Only the first ${MAX_CHAT_ATTACHMENTS} files were attached.`
                ),
                'Too many attachments'
              );
            }

            setFiles(selectedFiles.slice(0, MAX_CHAT_ATTACHMENTS));
            event.target.value = '';
          }}
        />
        <Tooltip title="Attach files">
          <span>
            <IconButton
              disabled={!channelId || isSending || isComposerDisabled}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                bgcolor: 'common.white',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                color: 'grey.900',
                height: 40,
                width: 40,
                '&:hover': {
                  bgcolor: 'common.white',
                },
              }}
            >
              <Iconify icon="eva:plus-fill" width={22} />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          variant="standard"
          inputRef={textInputRef}
          value={text}
          placeholder={placeholder}
          disabled={!channelId || isSending || isComposerDisabled}
          onChange={(event) => {
            setText(event.target.value);
            updateSelectionStart(event.target);
          }}
          onClick={(event) =>
            updateSelectionStart(
              event.target as HTMLInputElement | HTMLTextAreaElement
            )
          }
          onKeyUp={(event) =>
            updateSelectionStart(
              event.target as HTMLInputElement | HTMLTextAreaElement
            )
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter' && showMentionOptions) {
              event.preventDefault();
              handleSelectMention(mentionOptions[0]);
              return;
            }

            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void handleSend();
            }
          }}
          InputProps={{ disableUnderline: true }}
          sx={{
            '& .MuiInputBase-input': {
              color: 'grey.900',
              fontSize: 16,
              lineHeight: 1.4,
              py: 0,
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'text.disabled',
              opacity: 1,
            },
          }}
        />
        {showMentionOptions && (
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bottom: '100%',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
              left: 64,
              mb: 1,
              maxHeight: 220,
              overflowY: 'auto',
              position: 'absolute',
              width: 280,
              zIndex: 2,
            }}
          >
            <List disablePadding>
              {mentionOptions.map((mention) => (
                <ListItemButton
                  key={`${mention.participant_type}:${mention.participant_id}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSelectMention(mention);
                  }}
                  sx={{ px: 1.25, py: 1 }}
                >
                  <Typography variant="body2" noWrap>
                    @{mention.display_name}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}
        <Button
          disabled={!canSend || isSending || isComposerDisabled}
          onClick={() => void handleSend()}
          variant="contained"
          sx={{
            bgcolor: 'grey.900',
            borderRadius: 1.5,
            boxShadow: 'none',
            color: 'common.white',
            flexShrink: 0,
            minHeight: 44,
            minWidth: 88,
            px: 2.25,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'grey.800',
              boxShadow: 'none',
            },
          }}
        >
          {isSending ? <CircularProgress color="inherit" size={20} /> : 'Send'}
        </Button>
      </Stack>
    </Box>
  );
};

export default function CustomChatBar() {
  const { errorMsg } = useSnackbarMsg();
  const { isSuperAdmin, user } = useAuth();
  const queryClient = useQueryClient();
  const { activeChannelId, pendingOpenChatRequest } = useSelector(
    (state) => state.customChat
  );
  const processingRequestId = useRef<string | null>(null);
  const lastFocusedStatusRefreshAt = useRef(0);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const latestMessageRef = useRef<HTMLDivElement | null>(null);
  const scrollRestoreRef = useRef<{
    scrollHeight: number;
    scrollTop: number;
  } | null>(null);
  const isNearLatestMessageRef = useRef(true);
  const newestMessageIdRef = useRef<number | string | null>(null);
  const shouldScrollToLatestRef = useRef(false);
  const [activeNavView, setActiveNavView] =
    useState<CustomChatNavView>('channels');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedCreateUsers, setSelectedCreateUsers] = useState<User[]>([]);
  const [createSubjectError, setCreateSubjectError] = useState('');
  const [, setStatusRefreshAt] = useState(() => Date.now());
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [noticeDialogOpen, setNoticeDialogOpen] = useState(false);
  const [noticeText, setNoticeText] = useState('');
  const [noticeError, setNoticeError] = useState('');
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedTransferUser, setSelectedTransferUser] = useState<User | null>(
    null
  );
  const [transferError, setTransferError] = useState('');
  const [chatSearch, setChatSearch] = useState('');
  const [selectedGalleryImage, setSelectedGalleryImage] =
    useState<SelectedChatGalleryImage | null>(null);

  const channelsQuery = useCustomChatChannels(true, {
    refetchInterval: CUSTOM_CHAT_CHANNELS_ACTIVE_POLL_INTERVAL,
  });
  const createDashboardUsersQuery = useQuery({
    enabled: createDialogOpen,
    queryFn: getDashboardUsers,
    queryKey: ['customChat', 'createUsers', 'dashboard'],
  });
  const channels = useMemo(
    () => channelsQuery.data ?? [],
    [channelsQuery.data]
  );
  const createUserOptions = createDashboardUsersQuery.data ?? [];
  const isCreateUsersLoading = createDashboardUsersQuery.isLoading;
  const currentDashboardUserId = Number(user?.id);
  const currentDashboardMessageSenderId = Number.isFinite(
    currentDashboardUserId
  )
    ? currentDashboardUserId
    : null;
  const normalizedChatSearch = chatSearch.trim().toLowerCase();
  const filterChannelBySearch = (channel: CustomChatChannel) =>
    !normalizedChatSearch ||
    getChannelSearchText(channel, currentDashboardMessageSenderId).includes(
      normalizedChatSearch
    );
  const directMessageChannels = useMemo(
    () =>
      channels
        .filter((channel) => channel.type === 'directMessage')
        .filter(filterChannelBySearch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channels, normalizedChatSearch, currentDashboardMessageSenderId]
  );
  const supportChannels = useMemo(
    () =>
      channels
        .filter((channel) => channel.type !== 'directMessage')
        .filter(filterChannelBySearch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channels, normalizedChatSearch, currentDashboardMessageSenderId]
  );
  const jobSupportChannels = useMemo(
    () => supportChannels.filter((channel) => channel.type === 'jobSupport'),
    [supportChannels]
  );
  const jobSupportSessionIdsToFetch = useMemo(
    () => getCustomChatJobSupportSessionIdsToFetch(jobSupportChannels),
    [jobSupportChannels]
  );
  const jobSupportSessionQueries = useQueries({
    queries: jobSupportSessionIdsToFetch.map((sessionId) => ({
      queryKey: ['customChat', 'jobSupportGroupSession', sessionId],
      queryFn: () => getBookingSession(sessionId),
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const jobSupportSessionsById = useMemo(
    () =>
      jobSupportSessionQueries.reduce<Map<number, BookingSession>>(
        (sessionsById, query, index) => {
          const session = query.data as BookingSession | undefined;
          if (session) {
            sessionsById.set(jobSupportSessionIdsToFetch[index], session);
          }
          return sessionsById;
        },
        new Map()
      ),
    [jobSupportSessionIdsToFetch, jobSupportSessionQueries]
  );
  const supportChannelSections = useMemo(
    () =>
      SUPPORT_CHANNEL_SECTIONS.map((section) => ({
        ...section,
        channels: supportChannels.filter(
          (channel) => channel.type === section.type
        ),
      })).filter((section) => section.alwaysShow || section.channels.length),
    [supportChannels]
  );
  const visibleNavChannels =
    activeNavView === 'directMessages'
      ? directMessageChannels
      : supportChannels;
  const activeOpenedChannelQuery = useQuery({
    enabled: false,
    initialData: () =>
      queryClient.getQueryData<CustomChatChannel>(
        customChatChannelQueryKey(activeChannelId)
      ),
    queryFn: async () =>
      queryClient.getQueryData<CustomChatChannel>(
        customChatChannelQueryKey(activeChannelId)
      ) as CustomChatChannel,
    queryKey: customChatChannelQueryKey(activeChannelId),
  });
  const activeChannel =
    channels.find((channel) => channel.id === activeChannelId) ??
    activeOpenedChannelQuery.data ??
    visibleNavChannels[0] ??
    channels[0] ??
    null;
  const activeChannelMetadata = activeChannel?.metadata ?? {};
  const activeBookingSessionId = useMemo(
    () => resolveCustomChatBookingSessionId(activeChannel),
    [activeChannel]
  );
  const jobContextQuery = useQuery({
    enabled: Boolean(activeBookingSessionId),
    queryFn: () => getBookingSession(activeBookingSessionId as number),
    queryKey: ['customChat', 'jobContext', activeBookingSessionId],
    refetchOnWindowFocus: false,
    retry: false,
  });
  const jobContextLinks = useMemo(
    () =>
      buildCustomChatJobLinks(
        jobContextQuery.data,
        activeChannelMetadata,
        activeBookingSessionId
      ),
    [activeBookingSessionId, activeChannelMetadata, jobContextQuery.data]
  );
  const messagesQuery = useCustomChatMessages(activeChannel?.id ?? null, true);
  const openChannelMutation = useOpenCustomChatChannelMutation();
  const pickUpChannelMutation = usePickUpCustomChatChannelMutation();
  const transferChannelMutation = useTransferCustomChatChannelMutation();
  const markReadMutation = useMarkCustomChatReadMutation();
  const archiveMutation = useArchiveCustomChatChannelMutation();
  const addParticipantsMutation = useAddCustomChatParticipantsMutation(
    activeChannel?.id ?? null
  );
  const removeParticipantMutation = useRemoveCustomChatParticipantMutation(
    activeChannel?.id ?? null
  );
  const sendGeneralMessageMutation = useSendCustomChatGeneralMessageMutation(
    activeChannel?.id ?? null
  );
  const externalLockMutation = useSetCustomChatExternalLockMutation();
  const messageDays = useMemo(
    () => groupCustomChatMessagesByDay(messagesQuery.data ?? []),
    [messagesQuery.data]
  );
  const chatImageAttachments = useMemo<CustomChatGalleryAttachment[]>(
    () =>
      (messagesQuery.data ?? []).flatMap((message) =>
        (message.attachments ?? []).flatMap((attachment, attachmentIndex) => {
          if (!isImageAttachment(attachment) || !attachment.id) {
            return [];
          }

          return [
            {
              attachment,
              attachmentIndex,
              key: getChatImageGalleryKey(message, attachment, attachmentIndex),
              label: attachment.name || `Image ${attachmentIndex + 1}`,
              message,
            },
          ];
        })
      ),
    [messagesQuery.data]
  );
  const chatImageAccessUrlQueries = useQueries({
    queries: chatImageAttachments.map((galleryAttachment) => ({
      enabled: Boolean(
        galleryAttachment.message.channel_id && galleryAttachment.attachment.id
      ),
      queryFn: () =>
        getCustomChatAttachmentAccessUrl(
          galleryAttachment.message.channel_id,
          galleryAttachment.attachment.id as number | string
        ),
      queryKey: [
        'customChat',
        'attachmentAccessUrl',
        galleryAttachment.message.channel_id,
        galleryAttachment.attachment.id,
      ],
      staleTime: ATTACHMENT_ACCESS_URL_STALE_TIME_MS,
    })),
  });
  const chatGalleryImages = chatImageAttachments.reduce<
    CustomChatGalleryImage[]
  >((images, galleryAttachment, index) => {
    const signedUrl = (
      chatImageAccessUrlQueries[index]?.data as { url?: unknown } | undefined
    )?.url;

    if (typeof signedUrl === 'string' && isSafeAttachmentUrl(signedUrl)) {
      images.push({
        ...galleryAttachment,
        accessUrl: signedUrl,
      });
    }

    return images;
  }, []);
  const chatGalleryImageUrls = chatGalleryImages.map(
    (image) => image.accessUrl
  );
  const selectedGalleryImageIndex = selectedGalleryImage
    ? chatGalleryImages.findIndex(
        (image) => image.key === selectedGalleryImage.key
      )
    : -1;
  const activeGalleryImageUrls =
    selectedGalleryImageIndex >= 0
      ? chatGalleryImageUrls
      : selectedGalleryImage?.accessUrl
      ? [selectedGalleryImage.accessUrl]
      : [];
  const activeGalleryPhotoIndex =
    selectedGalleryImageIndex >= 0 ? selectedGalleryImageIndex : 0;
  const activeGalleryMainSrc =
    activeGalleryImageUrls[activeGalleryPhotoIndex] ??
    selectedGalleryImage?.accessUrl ??
    '';
  const handleOpenImageGallery = (image: SelectedChatGalleryImage) => {
    setSelectedGalleryImage(image);
  };
  const handleSetGalleryPhotoIndex = (imageIndex: number) => {
    if (selectedGalleryImageIndex < 0) return;

    const nextImage = chatGalleryImages[imageIndex];
    if (nextImage) {
      setSelectedGalleryImage({
        accessUrl: nextImage.accessUrl,
        key: nextImage.key,
      });
    }
  };
  const newestMessage = messagesQuery.data?.[messagesQuery.data.length - 1];
  const senderNameByKey = useMemo(() => {
    const names = new Map<string, string>();

    activeChannel?.participants?.forEach((participant) => {
      const participantName = getPersonDisplayName(participant);
      if (!participantName) return;

      names.set(
        `${participant.participant_type}:${participant.participant_id}`,
        participantName
      );
    });

    messagesQuery.data?.forEach((message) => {
      const senderName = getPersonDisplayName(message.sender);
      if (!senderName) return;

      const senderKey = getSenderKey(message);
      if (!names.has(senderKey)) {
        names.set(senderKey, senderName);
      }
    });

    const currentDashboardUserName = getPersonDisplayName(user);
    if (currentDashboardMessageSenderId && currentDashboardUserName) {
      names.set(
        `dashboard_user:${currentDashboardMessageSenderId}`,
        currentDashboardUserName
      );
    }

    if (
      (activeChannel?.type === 'dashboardSupport' ||
        activeChannel?.type === 'directMessage') &&
      activeChannel.title
    ) {
      messagesQuery.data?.forEach((message) => {
        if (
          message.sender_type !== 'dashboard_user' ||
          Number(message.sender_id) === currentDashboardMessageSenderId
        ) {
          return;
        }

        const senderKey = getSenderKey(message);
        const senderName = activeChannel.title?.trim();

        if (senderName && !names.has(senderKey)) {
          names.set(senderKey, senderName);
        }
      });
    }

    return names;
  }, [
    activeChannel?.participants,
    activeChannel?.title,
    activeChannel?.type,
    currentDashboardMessageSenderId,
    messagesQuery.data,
    user,
  ]);
  const senderImageByKey = useMemo(() => {
    const images = new Map<string, string>();

    activeChannel?.participants?.forEach((participant) => {
      const profileImage = getProfileImageUrl(participant);
      if (!profileImage) return;

      images.set(
        `${participant.participant_type}:${participant.participant_id}`,
        profileImage
      );
    });

    messagesQuery.data?.forEach((message) => {
      const profileImage = getProfileImageUrl(message.sender);
      if (!profileImage) return;

      const senderKey = getSenderKey(message);
      if (!images.has(senderKey)) {
        images.set(senderKey, profileImage);
      }
    });

    const currentDashboardUserImage = getProfileImageUrl(user);
    if (currentDashboardMessageSenderId && currentDashboardUserImage) {
      images.set(
        `dashboard_user:${currentDashboardMessageSenderId}`,
        currentDashboardUserImage
      );
    }

    return images;
  }, [
    activeChannel?.participants,
    currentDashboardMessageSenderId,
    messagesQuery.data,
    user,
  ]);
  const memberStatus = getMemberStatus(
    activeChannel?.participants,
    currentDashboardMessageSenderId,
    senderNameByKey
  );
  const activeParticipantCount = activeChannel?.participants?.length ?? 0;
  const activeChannelDisplayTitle = activeChannel
    ? getDisplayChannelTitle(activeChannel, currentDashboardMessageSenderId)
    : '';
  const activeCurrentParticipant = activeChannel
    ? getCurrentChannelParticipant(
        activeChannel,
        currentDashboardMessageSenderId
      )
    : null;
  const activeSupportAssignmentLabel = activeChannel
    ? getSupportAssignmentLabel(activeChannel, currentDashboardMessageSenderId)
    : null;
  const activeSupportChannelCanBeHandled = activeChannel
    ? canHandleSupportChannel(
        activeChannel,
        currentDashboardMessageSenderId,
        isSuperAdmin
      )
    : true;
  const activeSupportChannelCanBeTransferred = activeChannel
    ? canTransferSupportChannel(
        activeChannel,
        currentDashboardMessageSenderId,
        isSuperAdmin
      )
    : false;
  const activeSupportComposerDisabledReason =
    activeChannel && !activeSupportChannelCanBeHandled
      ? getSupportComposerDisabledReason(
          activeChannel,
          currentDashboardMessageSenderId
        )
      : null;
  const isExternalParticipant =
    activeCurrentParticipant?.participant_type === 'client' ||
    activeCurrentParticipant?.participant_type === 'cleaner';
  const isComposerExternallyLocked = Boolean(
    activeChannel?.external_locked && isExternalParticipant
  );
  const isComposerSupportDisabled = Boolean(
    activeChannel && !activeSupportChannelCanBeHandled
  );
  const composerDisabledReason =
    activeSupportComposerDisabledReason ??
    (isComposerExternallyLocked
      ? 'This channel is closed for discussions.'
      : null);
  const canManageJobSupportChannel = Boolean(
    isSuperAdmin && activeChannel?.type === 'jobSupport'
  );
  const canManageParticipants = Boolean(
    activeChannel && activeSupportChannelCanBeHandled
  );
  const participantManagementUsersQuery = useQuery({
    enabled: participantsDialogOpen && canManageParticipants,
    queryFn: getDashboardUsers,
    queryKey: ['customChat', 'participantUsers', 'dashboard'],
  });
  const transferUsersQuery = useQuery({
    enabled: transferDialogOpen && activeSupportChannelCanBeTransferred,
    queryFn: getDashboardUsers,
    queryKey: ['customChat', 'transferUsers', 'dashboard'],
  });
  const numericAssignedDashboardUserId = Number(
    activeChannel?.assigned_dashboard_user_id
  );
  const activeAssignedDashboardUserId = Number.isFinite(
    numericAssignedDashboardUserId
  )
    ? numericAssignedDashboardUserId
    : null;
  const activeDashboardParticipantIds = useMemo(
    () =>
      new Set(
        (activeChannel?.participants ?? [])
          .filter(
            (participant) => participant.participant_type === 'dashboard_user'
          )
          .map((participant) => Number(participant.participant_id))
      ),
    [activeChannel?.participants]
  );
  const addableParticipantUsers = useMemo(
    () =>
      (participantManagementUsersQuery.data ?? []).filter(
        (dashboardUser) => !activeDashboardParticipantIds.has(dashboardUser.id)
      ),
    [activeDashboardParticipantIds, participantManagementUsersQuery.data]
  );
  const transferUserOptions = useMemo(
    () =>
      (transferUsersQuery.data ?? []).filter(
        (dashboardUser) =>
          isActiveDashboardUser(dashboardUser) &&
          dashboardUser.id !== activeAssignedDashboardUserId
      ),
    [activeAssignedDashboardUserId, transferUsersQuery.data]
  );
  const removingParticipant = removeParticipantMutation.variables;
  const removingParticipantKey = removingParticipant
    ? `${removingParticipant.participant_type}:${removingParticipant.participant_id}`
    : null;
  const typingParticipantNames: string[] = [];

  const refreshFocusedChatStatus = () => {
    const now = Date.now();

    if (
      now - lastFocusedStatusRefreshAt.current <
      FOCUSED_CHAT_STATUS_REFRESH_THROTTLE_MS
    ) {
      return;
    }

    lastFocusedStatusRefreshAt.current = now;
    setStatusRefreshAt(now);
    void channelsQuery.refetch();

    if (!activeChannel?.id || !messagesQuery.data?.length) return;
    markReadMutation.mutate(activeChannel.id);
  };

  useEffect(() => {
    const unreadCount = channels.reduce(
      (total, channel) => total + Number(channel.unread_count ?? 0),
      0
    );
    dispatch(updateCustomChatUnreadMessages({ unreadCount }));
  }, [channels]);

  useEffect(() => {
    if (!activeChannelId && activeChannel?.id) {
      dispatch(setCustomChatActiveChannelId(activeChannel.id));
    }
  }, [activeChannel?.id, activeChannelId]);

  useEffect(() => {
    if (!pendingOpenChatRequest) return;
    if (processingRequestId.current === pendingOpenChatRequest.requestId) {
      return;
    }

    processingRequestId.current = pendingOpenChatRequest.requestId;

    const openChat = async () => {
      try {
        const { requestId: _requestId, ...payload } = pendingOpenChatRequest;
        const channel = await openChannelMutation.mutateAsync(payload);
        setActiveNavView('channels');
        dispatch(setCustomChatActiveChannelId(channel.id));
      } catch (error) {
        errorMsg(error, 'Unable to open custom chat');
      } finally {
        dispatch(clearCustomChatOpenRequest());
        processingRequestId.current = null;
      }
    };

    void openChat();
  }, [errorMsg, openChannelMutation, pendingOpenChatRequest]);

  useEffect(() => {
    if (!activeChannel?.id || !messagesQuery.data?.length) return;
    if (document.visibilityState !== 'visible') return;
    markReadMutation.mutate(activeChannel.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel?.id, messagesQuery.data?.length]);

  useEffect(() => {
    setSelectedGalleryImage(null);
    scrollRestoreRef.current = null;
    isNearLatestMessageRef.current = true;
    newestMessageIdRef.current = null;
    shouldScrollToLatestRef.current = true;
  }, [activeChannel?.id]);

  useEffect(() => {
    const newestMessageId = newestMessage?.id ?? null;

    if (!newestMessageId || newestMessageIdRef.current === newestMessageId) {
      return;
    }

    const shouldFollowNewestMessage =
      shouldScrollToLatestRef.current ||
      isNearLatestMessageRef.current ||
      Boolean(
        newestMessage &&
          isCurrentDashboardMessage(
            newestMessage,
            activeCurrentParticipant,
            currentDashboardMessageSenderId
          )
      );

    newestMessageIdRef.current = newestMessageId;

    if (shouldFollowNewestMessage && !messagesQuery.isLoadingOlder) {
      shouldScrollToLatestRef.current = true;
    }
  }, [
    activeCurrentParticipant,
    currentDashboardMessageSenderId,
    messagesQuery.isLoadingOlder,
    newestMessage,
  ]);

  useEffect(() => {
    const scrollRestore = scrollRestoreRef.current;
    const messageContainer = messageContainerRef.current;

    if (!scrollRestore || !messageContainer || messagesQuery.isLoadingOlder) {
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      const nextMessageContainer = messageContainerRef.current;

      if (!nextMessageContainer) return;

      nextMessageContainer.scrollTop =
        nextMessageContainer.scrollHeight -
        scrollRestore.scrollHeight +
        scrollRestore.scrollTop;
      scrollRestoreRef.current = null;
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [messagesQuery.data?.length, messagesQuery.isLoadingOlder]);

  useEffect(() => {
    const messageContainer = messageContainerRef.current;

    if (
      !shouldScrollToLatestRef.current ||
      !messageContainer ||
      messagesQuery.isLoading ||
      messagesQuery.isLoadingOlder ||
      scrollRestoreRef.current
    ) {
      return undefined;
    }

    const scrollToLatestMessage = () => {
      const latestMessage = latestMessageRef.current;
      const nextMessageContainer = messageContainerRef.current;

      if (latestMessage) {
        latestMessage.scrollIntoView({ block: 'end' });
        return;
      }

      if (nextMessageContainer) {
        nextMessageContainer.scrollTop = nextMessageContainer.scrollHeight;
      }
    };

    const animationFrameId = window.requestAnimationFrame(() => {
      scrollToLatestMessage();
    });
    const settleTimeoutIds = [50, 150, 350, 700].map((delay) =>
      window.setTimeout(scrollToLatestMessage, delay)
    );
    const finishTimeoutId = window.setTimeout(() => {
      shouldScrollToLatestRef.current = false;
    }, 800);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      settleTimeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      window.clearTimeout(finishTimeoutId);
    };
  }, [
    activeChannel?.id,
    messagesQuery.data?.length,
    messagesQuery.isLoading,
    messagesQuery.isLoadingOlder,
  ]);

  useEffect(() => {
    const handleWindowFocus = () => {
      refreshFocusedChatStatus();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshFocusedChatStatus();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel?.id, messagesQuery.data?.length]);

  const handleArchive = async () => {
    if (!activeChannel?.id) return;

    try {
      await archiveMutation.mutateAsync({ channelId: activeChannel.id });
      dispatch(setCustomChatActiveChannelId(null));
    } catch (error) {
      errorMsg(error, 'Unable to archive custom chat');
    }
  };

  const handleMessageContainerScroll = () => {
    const messageContainer = messageContainerRef.current;

    if (messageContainer) {
      isNearLatestMessageRef.current =
        messageContainer.scrollHeight -
          messageContainer.scrollTop -
          messageContainer.clientHeight <=
        SCROLL_TO_LATEST_BOTTOM_THRESHOLD_PX;
    }

    if (
      !messageContainer ||
      messageContainer.scrollTop > LOAD_OLDER_MESSAGES_SCROLL_THRESHOLD_PX ||
      messagesQuery.isLoading ||
      messagesQuery.isLoadingOlder ||
      !messagesQuery.hasOlderMessages
    ) {
      return;
    }

    scrollRestoreRef.current = {
      scrollHeight: messageContainer.scrollHeight,
      scrollTop: messageContainer.scrollTop,
    };

    void messagesQuery.loadOlderMessages();
  };

  const handleCloseNoticeDialog = () => {
    if (sendGeneralMessageMutation.isLoading) return;
    setNoticeDialogOpen(false);
    setNoticeText('');
    setNoticeError('');
  };

  const handleSendNotice = async () => {
    const trimmedNoticeText = noticeText.trim();

    if (!activeChannel?.id) return;
    if (!trimmedNoticeText) {
      setNoticeError('Enter a notice message.');
      return;
    }

    try {
      await sendGeneralMessageMutation.mutateAsync({ text: trimmedNoticeText });
      setNoticeDialogOpen(false);
      setNoticeText('');
      setNoticeError('');
    } catch (error) {
      errorMsg(error, 'Unable to send custom chat notice');
    }
  };

  const handleToggleExternalLock = async () => {
    if (!activeChannel?.id) return;

    try {
      await externalLockMutation.mutateAsync({
        channelId: activeChannel.id,
        locked: !activeChannel.external_locked,
      });
    } catch (error) {
      errorMsg(error, 'Unable to update external chat lock');
    }
  };

  const handlePickUpSupportChat = async () => {
    if (!activeChannel?.id) return;

    try {
      const channel = await pickUpChannelMutation.mutateAsync(activeChannel.id);
      dispatch(setCustomChatActiveChannelId(channel.id));
      void channelsQuery.refetch();
    } catch (error) {
      errorMsg(error, 'Unable to pick up custom chat');
      void channelsQuery.refetch();
    }
  };

  const handleCloseTransferDialog = () => {
    if (transferChannelMutation.isLoading) return;
    setTransferDialogOpen(false);
    setSelectedTransferUser(null);
    setTransferError('');
  };

  const handleOpenTransferDialog = () => {
    setSelectedTransferUser(null);
    setTransferError('');
    setTransferDialogOpen(true);
  };

  const handleTransferSupportChat = async () => {
    if (!activeChannel?.id) return;

    if (!selectedTransferUser) {
      setTransferError('Select a dashboard user.');
      return;
    }

    try {
      const channel = await transferChannelMutation.mutateAsync({
        channelId: activeChannel.id,
        payload: { dashboard_user_id: selectedTransferUser.id },
      });
      dispatch(setCustomChatActiveChannelId(channel.id));
      setTransferDialogOpen(false);
      setSelectedTransferUser(null);
      setTransferError('');
      void channelsQuery.refetch();
    } catch (error) {
      errorMsg(error, 'Unable to transfer custom chat');
      void channelsQuery.refetch();
    }
  };

  const handleCloseCreateDialog = () => {
    if (openChannelMutation.isLoading) return;
    setCreateDialogOpen(false);
    setCreateSubjectError('');
    setSelectedCreateUsers([]);
  };

  const handleOpenDirectMessageDialog = () => {
    setSelectedCreateUsers([]);
    setCreateSubjectError('');
    setCreateDialogOpen(true);
  };

  const handleCreateChat = async () => {
    const dashboardUserIds = selectedCreateUsers.map(
      (selectedUser) => selectedUser.id
    );

    if (!dashboardUserIds.length) {
      setCreateSubjectError('Select at least one user.');
      return;
    }

    try {
      const channel = await openChannelMutation.mutateAsync({
        dashboard_user_ids: dashboardUserIds,
        type: 'directMessage',
      });
      setActiveNavView('channels');
      dispatch(setCustomChatActiveChannelId(channel.id));
      setCreateDialogOpen(false);
      setCreateSubjectError('');
      setSelectedCreateUsers([]);
    } catch (error) {
      errorMsg(error, 'Unable to create custom chat');
    }
  };

  const handleAddParticipants = async (usersToAdd: User[]) => {
    if (!activeChannel?.id) return;

    try {
      await addParticipantsMutation.mutateAsync({
        participants: usersToAdd.map((userToAdd) => ({
          participant_id: userToAdd.id,
          participant_type: 'dashboard_user',
        })),
      });
    } catch (error) {
      errorMsg(error, 'Unable to add custom chat participants');
    }
  };

  const handleRemoveParticipant = async (
    participant: CustomChatParticipantIdentity
  ) => {
    if (!activeChannel?.id) return;

    try {
      await removeParticipantMutation.mutateAsync(participant);
    } catch (error) {
      errorMsg(error, 'Unable to remove custom chat participant');
    }
  };

  return (
    <Box
      onFocusCapture={refreshFocusedChatStatus}
      sx={{
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        minHeight: 0,
        width: 1,
      }}
    >
      <RightSidebarTitle title="Chat" />
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            width: 320,
          }}
        >
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {channelsQuery.isLoading ? (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : channelsQuery.isError ? (
              <Alert severity="error" sx={{ m: 1 }}>
                Unable to load custom chats.
              </Alert>
            ) : (
              <>
                <Box sx={{ px: 2, pb: 1.5, pt: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={chatSearch}
                    placeholder="Search chats"
                    onChange={(event) => setChatSearch(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Iconify
                          icon="eva:search-fill"
                          width={18}
                          sx={{ color: 'text.disabled', mr: 0.75 }}
                        />
                      ),
                      endAdornment: chatSearch ? (
                        <IconButton
                          aria-label="Clear chat search"
                          edge="end"
                          size="small"
                          onClick={() => setChatSearch('')}
                        >
                          <Iconify icon="eva:close-fill" width={16} />
                        </IconButton>
                      ) : null,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'grey.100',
                        borderRadius: 1.5,
                      },
                    }}
                  />
                </Box>
                <List disablePadding>
                  <ChatNavItem
                    active={activeNavView === 'channels'}
                    count={supportChannels.length}
                    icon="eva:hash-fill"
                    label="Channels"
                    onClick={() => setActiveNavView('channels')}
                  />
                </List>
                <Box sx={{ mt: 0.5 }}>
                  {supportChannelSections.length ? (
                    <Stack spacing={1.25}>
                      {supportChannelSections.map((section) => (
                        <Box key={section.type}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ px: 2, pt: 1.25, pb: 0.75 }}
                          >
                            {CHANNEL_TYPE_LABELS[section.type]}
                          </Typography>
                          {section.channels.length ? (
                            section.type === 'jobSupport' ? (
                              <CustomChatJobSupportGroups
                                activeChannelId={activeChannel?.id}
                                channels={section.channels}
                                sessionsById={jobSupportSessionsById}
                                onSelect={(channelId) => {
                                  setActiveNavView('channels');
                                  dispatch(
                                    setCustomChatActiveChannelId(channelId)
                                  );
                                }}
                              />
                            ) : (
                              <List disablePadding>
                                {section.channels.map((channel) => (
                                  <ChannelListItem
                                    key={channel.id}
                                    channel={channel}
                                    currentDashboardUserId={
                                      currentDashboardMessageSenderId
                                    }
                                    isActive={channel.id === activeChannel?.id}
                                    onSelect={(channelId) => {
                                      setActiveNavView('channels');
                                      dispatch(
                                        setCustomChatActiveChannelId(channelId)
                                      );
                                    }}
                                  />
                                ))}
                              </List>
                            )
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ px: 2, py: 0.5 }}
                            >
                              {section.emptyText}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ px: 2, py: 1 }}
                    >
                      {normalizedChatSearch
                        ? 'No matching support chats.'
                        : 'No support channels yet.'}
                    </Typography>
                  )}
                </Box>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ px: 2, pt: 3, pb: 1 }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ flex: 1 }}
                  >
                    Direct Messages
                  </Typography>
                  <Tooltip title="New direct message">
                    <IconButton
                      aria-label="New direct message"
                      size="small"
                      onClick={handleOpenDirectMessageDialog}
                      sx={{
                        color: 'text.secondary',
                        height: 28,
                        width: 28,
                      }}
                    >
                      <Iconify icon="eva:plus-fill" width={18} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                {directMessageChannels.length ? (
                  <List disablePadding>
                    {directMessageChannels.map((channel) => (
                      <DirectMessageListItem
                        key={channel.id}
                        channel={channel}
                        currentDashboardUserId={currentDashboardMessageSenderId}
                        isActive={channel.id === activeChannel?.id}
                        onSelect={(channelId) => {
                          setActiveNavView('channels');
                          dispatch(setCustomChatActiveChannelId(channelId));
                        }}
                      />
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ px: 2, py: 1 }}
                  >
                    {normalizedChatSearch
                      ? 'No matching direct messages.'
                      : 'No direct messages yet.'}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          {activeChannel ? (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 2 }}>
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap>
                      {activeChannelDisplayTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {CHANNEL_TYPE_LABELS[activeChannel.type]}
                    </Typography>
                    {activeSupportAssignmentLabel && (
                      <Chip
                        color={getSupportAssignmentChipColor(
                          activeChannel,
                          currentDashboardMessageSenderId
                        )}
                        label={activeSupportAssignmentLabel}
                        size="small"
                        sx={{
                          maxWidth: 1,
                          mt: 0.75,
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                        variant={
                          isAssignedSupportChannel(activeChannel)
                            ? 'outlined'
                            : 'filled'
                        }
                      />
                    )}
                    {canManageJobSupportChannel &&
                      activeChannel.external_locked && (
                        <Chip
                          color="warning"
                          label="External locked"
                          size="small"
                          sx={{ mt: 0.75 }}
                        />
                      )}
                    <MemberStatusIndicator status={memberStatus} />
                    <JobMetadata channel={activeChannel} />
                  </Box>
                  {canManageJobSupportChannel && (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      justifyContent="flex-end"
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Iconify icon="eva:message-square-fill" />}
                        onClick={() => setNoticeDialogOpen(true)}
                      >
                        Send notice
                      </Button>
                      <Button
                        color={
                          activeChannel.external_locked ? 'success' : 'warning'
                        }
                        disabled={externalLockMutation.isLoading}
                        size="small"
                        variant="outlined"
                        startIcon={
                          <Iconify
                            icon={
                              activeChannel.external_locked
                                ? 'eva:unlock-fill'
                                : 'eva:lock-fill'
                            }
                          />
                        }
                        onClick={() => void handleToggleExternalLock()}
                      >
                        {activeChannel.external_locked
                          ? 'Unlock external chat'
                          : 'Lock external chat'}
                      </Button>
                    </Stack>
                  )}
                  {isPersonSupportChannel(activeChannel) &&
                    activeChannel.can_pick_up && (
                      <Button
                        disabled={pickUpChannelMutation.isLoading}
                        size="small"
                        variant="contained"
                        startIcon={<Iconify icon="eva:person-add-fill" />}
                        onClick={() => void handlePickUpSupportChat()}
                        sx={{
                          borderRadius: 1,
                          boxShadow: 'none',
                          fontWeight: 700,
                          textTransform: 'none',
                        }}
                      >
                        {pickUpChannelMutation.isLoading
                          ? 'Picking up...'
                          : 'Pick up'}
                      </Button>
                    )}
                  {activeSupportChannelCanBeTransferred && (
                    <Button
                      disabled={transferChannelMutation.isLoading}
                      size="small"
                      variant="outlined"
                      startIcon={<Iconify icon="eva:swap-fill" />}
                      onClick={handleOpenTransferDialog}
                      sx={{
                        borderRadius: 1,
                        fontWeight: 700,
                        textTransform: 'none',
                      }}
                    >
                      Transfer
                    </Button>
                  )}
                  <Tooltip title="View participants">
                    <span>
                      <IconButton
                        disabled={!activeSupportChannelCanBeHandled}
                        onClick={() => setParticipantsDialogOpen(true)}
                        sx={{
                          height: 40,
                          position: 'relative',
                          width: 40,
                        }}
                      >
                        <Iconify icon="eva:people-fill" />
                        {activeParticipantCount > 0 && (
                          <Box
                            component="span"
                            sx={{
                              alignItems: 'center',
                              bgcolor: 'background.paper',
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 999,
                              color: 'text.secondary',
                              display: 'inline-flex',
                              fontSize: 11,
                              fontWeight: 700,
                              height: 17,
                              justifyContent: 'center',
                              minWidth: 17,
                              px: 0.5,
                              position: 'absolute',
                              right: 1,
                              top: 1,
                            }}
                          >
                            {activeParticipantCount}
                          </Box>
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Archive custom chat">
                    <span>
                      <IconButton
                        disabled={
                          archiveMutation.isLoading ||
                          !activeSupportChannelCanBeHandled
                        }
                        onClick={() => void handleArchive()}
                      >
                        <Iconify icon="eva:archive-outline" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
              <Box
                ref={messageContainerRef}
                onScroll={handleMessageContainerScroll}
                sx={{
                  bgcolor: 'background.paper',
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  px: 3,
                  py: 2,
                }}
              >
                {messagesQuery.isLoading ? (
                  <Stack alignItems="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </Stack>
                ) : messagesQuery.isError ? (
                  <Alert severity="error">Unable to load messages.</Alert>
                ) : messageDays.length ? (
                  <Stack spacing={2.75}>
                    {messagesQuery.isLoadingOlder && (
                      <Stack alignItems="center" sx={{ py: 0.5 }}>
                        <CircularProgress size={18} />
                      </Stack>
                    )}
                    {messageDays.map((day) => (
                      <Stack key={day.id} spacing={2.75}>
                        <MessageDateSeparator label={day.label} />
                        {day.groups.map((group) => {
                          const generalMessage = group.messages.find(
                            isCustomChatGeneralMessage
                          );

                          if (generalMessage) {
                            return (
                              <GeneralMessageNotice
                                key={group.id}
                                message={generalMessage}
                              />
                            );
                          }

                          return (
                            <MessageGroup
                              key={group.id}
                              group={group}
                              currentParticipant={activeCurrentParticipant}
                              currentDashboardUserId={
                                currentDashboardMessageSenderId
                              }
                              onOpenImageGallery={handleOpenImageGallery}
                              participants={activeChannel.participants}
                              senderImageByKey={senderImageByKey}
                              senderNameByKey={senderNameByKey}
                            />
                          );
                        })}
                      </Stack>
                    ))}
                    {typingParticipantNames.map((typingParticipantName) => (
                      <MessageTypingIndicator
                        key={typingParticipantName}
                        senderName={typingParticipantName}
                      />
                    ))}
                    <Box ref={latestMessageRef} sx={{ height: 1 }} />
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No messages yet.
                  </Typography>
                )}
              </Box>
              <MessageComposer
                channelId={activeChannel.id}
                currentDashboardUserId={currentDashboardMessageSenderId}
                currentParticipant={activeCurrentParticipant}
                disabledReason={composerDisabledReason}
                isDisabled={isComposerSupportDisabled}
                isExternallyLocked={isComposerExternallyLocked}
                participants={activeChannel.participants}
                placeholderTarget={activeChannelDisplayTitle}
              />
            </>
          ) : (
            <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Select a custom chat to begin.
              </Typography>
            </Stack>
          )}
        </Box>
        {activeChannel?.type === 'jobSupport' && (
          <CustomChatJobDetailsPanel
            bookingSessionId={activeBookingSessionId}
            isError={jobContextQuery.isError}
            isLoading={jobContextQuery.isLoading}
            links={jobContextLinks}
            metadata={activeChannelMetadata}
            session={jobContextQuery.data}
          />
        )}
      </Box>

      <Dialog
        fullWidth
        maxWidth="xs"
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
      >
        <DialogTitle>New Direct Message</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Autocomplete
              multiple
              options={createUserOptions}
              loading={isCreateUsersLoading}
              value={selectedCreateUsers}
              onChange={(_event, value) => {
                setSelectedCreateUsers(value);
                setCreateSubjectError('');
              }}
              getOptionLabel={getUserOptionLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) => {
                const search = state.inputValue.trim().toLowerCase();
                if (!search) return options;

                return options.filter((option) =>
                  [
                    option.id,
                    option.first_name,
                    option.last_name,
                    option.email,
                    option.phone,
                  ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                    .includes(search)
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dashboard users"
                  error={Boolean(createSubjectError)}
                  helperText={
                    createSubjectError || 'Select one or more dashboard users.'
                  }
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack spacing={0.25}>
                    <Typography variant="body2">
                      {getUserOptionLabel(option)}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={openChannelMutation.isLoading}
            onClick={handleCloseCreateDialog}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={openChannelMutation.isLoading}
            onClick={() => void handleCreateChat()}
          >
            {openChannelMutation.isLoading
              ? 'Creating...'
              : 'Create direct message'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="xs"
        open={noticeDialogOpen}
        onClose={handleCloseNoticeDialog}
      >
        <DialogTitle>Send notice</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Notice message"
            value={noticeText}
            error={Boolean(noticeError)}
            helperText={noticeError || 'This will appear centered in the chat.'}
            sx={{ mt: 1 }}
            onChange={(event) => {
              setNoticeText(event.target.value);
              setNoticeError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                void handleSendNotice();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={sendGeneralMessageMutation.isLoading}
            onClick={handleCloseNoticeDialog}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={sendGeneralMessageMutation.isLoading}
            onClick={() => void handleSendNotice()}
          >
            {sendGeneralMessageMutation.isLoading ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="xs"
        open={transferDialogOpen}
        onClose={handleCloseTransferDialog}
      >
        <DialogTitle>Transfer chat</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Autocomplete
              options={transferUserOptions}
              loading={transferUsersQuery.isLoading}
              value={selectedTransferUser}
              onChange={(_event, value) => {
                setSelectedTransferUser(value);
                setTransferError('');
              }}
              getOptionLabel={getUserOptionLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) => {
                const search = state.inputValue.trim().toLowerCase();
                if (!search) return options;

                return options.filter((option) =>
                  [
                    option.id,
                    option.first_name,
                    option.last_name,
                    option.email,
                    option.phone,
                  ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                    .includes(search)
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Transfer to"
                  error={Boolean(transferError)}
                  helperText={
                    transferError || 'Select an active dashboard user.'
                  }
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack spacing={0.25}>
                    <Typography variant="body2">
                      {getUserOptionLabel(option)}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={transferChannelMutation.isLoading}
            onClick={handleCloseTransferDialog}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              transferChannelMutation.isLoading || !selectedTransferUser
            }
            onClick={() => void handleTransferSupportChat()}
          >
            {transferChannelMutation.isLoading ? 'Transferring...' : 'Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      <LightboxModal
        images={activeGalleryImageUrls}
        mainSrc={activeGalleryMainSrc}
        photoIndex={activeGalleryPhotoIndex}
        setPhotoIndex={handleSetGalleryPhotoIndex}
        isOpen={Boolean(selectedGalleryImage && activeGalleryMainSrc)}
        onCloseRequest={() => setSelectedGalleryImage(null)}
      />

      <ParticipantsDialog
        addableUsers={addableParticipantUsers}
        canManageParticipants={canManageParticipants}
        currentDashboardUserId={currentDashboardMessageSenderId}
        isAddingParticipants={addParticipantsMutation.isLoading}
        isLoadingUsers={participantManagementUsersQuery.isLoading}
        isRemovingParticipant={removeParticipantMutation.isLoading}
        open={participantsDialogOpen}
        onClose={() => setParticipantsDialogOpen(false)}
        onAddParticipants={(usersToAdd) =>
          void handleAddParticipants(usersToAdd)
        }
        onRemoveParticipant={(participant) =>
          void handleRemoveParticipant(participant)
        }
        participants={activeChannel?.participants}
        removingParticipantKey={removingParticipantKey}
      />
    </Box>
  );
}
